/**
 * systems.ts - Tick Systems
 *
 * Pure functions: (state, deltaMs) => newState
 * No browser APIs allowed here.
 *
 * Includes roller-coaster-tycoon style visitor satisfaction algorithm.
 */

import type { GameStateData, VisitorGroup, Visitor, ScarePopup, VisitorReaction, VisitorPhase, QueueVisitor, ActiveEvent, StreetVisitor, StreetVisitorNeeds, StreetVisitorType, SeasonEvent, SeasonEventId, VipRequirement, VipRequirementType } from './state.js';
import { GHOST_DEFS } from './data/ghosts.js';
import { ROOM_TYPES } from './data/rooms.js';
import { getRoomGhostSynergyMultiplier } from './data/roomGhostSynergies.js';
import { HAUNTED_THEMES } from './data/themes.js';
import { getSelectedBuildingThemeId } from './state.js';
import {
  VISITOR_TYPES,
  calcSatisfactionCurve,
  calcNausea,
  shouldVisitorFlee,
} from './data/visitors.js';
import { FACILITY_DEFS } from './data/facilities.js';
import { FACILITY_PROP_DEFS } from './data/facilityProps.js';
import { PROP_DEFS } from './data/props.js';
import { calcRoomStats, calcStatSatisfaction } from './roomStats.js';
import { calcReviewStars, pickReviewTemplate, fameFromReview } from './data/reviews.js';
import type { FearType } from './data/rooms.js';
import type { VisitorTypeId } from './data/visitors.js';
import { CONFIG } from './config.js';
import { LOBBY_FACILITY_DEFS } from './data/lobbyFacilities.js';
import { getEligibleEvents } from './data/events.js';

// ── Scare Calculation ──────────────────────────────────────

function calcAffinityBonus(ghostFearTypes: FearType[], roomAffinities: { fearType: FearType; bonus: number }[]): number {
  let totalBonus = 0;
  for (const affinity of roomAffinities) {
    if (ghostFearTypes.includes(affinity.fearType)) {
      totalBonus += affinity.bonus;
    }
  }
  if (totalBonus >= 0.4) return CONFIG.affinityPerfect;
  if (totalBonus >= 0.2) return CONFIG.affinityGood;
  if (totalBonus > 0) return CONFIG.affinityNone;
  return CONFIG.affinityBad;
}

function calcChainBonus(chainCount: number): number {
  const bonuses = CONFIG.chainScareBonus;
  const idx = Math.min(chainCount, bonuses.length - 1);
  return bonuses[idx] ?? 0;
}

function calcPropBonus(state: GameStateData, roomId: string): number {
  let bonus = 0;
  for (const floor of state.building) {
    const r = floor.room;
    if (r && r.id === roomId) {
      for (const prop of r.props) {
        const propDef = PROP_DEFS[prop.propId];
        if (propDef) {
          bonus += propDef.scareBonus * prop.level;
        }
      }
      return bonus;
    }
  }
  return bonus;
}

/**
 * Time phase multiplier for scare effectiveness.
 * Night: ghosts are scarier. Day: less effective. Rain: moderate bonus.
 */
function timePhaseScareMultiplier(phase: 'day' | 'night' | 'rain'): number {
  switch (phase) {
    case 'night': return 1.3;
    case 'rain': return 1.1;
    case 'day':
    default: return 1.0;
  }
}

interface ScareResult {
  sp: number;
  skillActivated: boolean;
}

function calcSpEarned(
  state: GameStateData,
  roomId: string,
  ghostInstanceId: string,
  visitors: Visitor[],
  chainCount: number,
): ScareResult {
  const noResult: ScareResult = { sp: 0, skillActivated: false };
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return noResult;

  const ghostDef = GHOST_DEFS[ghost.characterId];
  if (!ghostDef) return noResult;

  // Find the room
  let room = null;
  for (const floor of state.building) {
    if (floor.room && floor.room.id === roomId) { room = floor.room; break; }
  }
  if (!room) return noResult;

  const roomDef = ROOM_TYPES[room.type];
  if (!roomDef) return noResult;
  const selectedThemeId = getSelectedBuildingThemeId(state);
  const themeDef = selectedThemeId ? HAUNTED_THEMES[selectedThemeId] : null;
  const upgradeMultiplier = CONFIG.roomUpgradeMultipliers[room.upgradeLevel - 1] ?? 1.0;
  const affinityMultiplier = calcAffinityBonus(ghostDef.fearTypes, roomDef.baseAffinities);
  const levelMultiplier = 1 + (ghost.level - 1) * CONFIG.ghostLevelBonusPerLevel;
  const fatigueMultiplier = ghost.fatigue >= CONFIG.fatiguePenaltyThreshold
    ? CONFIG.fatiguePenaltyMultiplier
    : 1.0;
  const chainBonus = 1 + calcChainBonus(chainCount);
  const prestigeBonus = 1 + state.prestigeLevel * CONFIG.prestigeGhostBonusPerLevel;
  const trainingBonus = 1 + ghost.trainingBonus;
  const propBonus = 1 + calcPropBonus(state, roomId);
  const roomGhostSynergy = getRoomGhostSynergyMultiplier(room.type, ghost.characterId);
  const roomThemeBonus = selectedThemeId && roomDef.themeTags?.includes(selectedThemeId) ? 1.15 : 1.0;
  const ghostThemeBonus = selectedThemeId && ghostDef.themeTags?.includes(selectedThemeId) ? 1.1 : 1.0;
  const preferredThemePairBonus = themeDef && themeDef.preferredRoomTypes.includes(room.type) && themeDef.preferredGhostIds.includes(ghost.characterId) ? 1.1 : 1.0;
  const timePhaseMult = timePhaseScareMultiplier(state.timePhase);

  // Ad boost check
  const now = Date.now();
  const hasSpDoubleBoost = state.adBoosts.some(
    (b) => b.type === 'sp_double' && b.expiresAt > now,
  );
  const adMultiplier = hasSpDoubleBoost ? 2.0 : 1.0;

  // Facility: snack_bar increases visitor rewards
  const snackBar = state.facilities.find((f) => f.facilityId === 'snack_bar');
  const snackBarBonus = snackBar ? 1 + snackBar.level * 0.05 : 1.0;

  // Active skill check
  let activeSkillBonus = 1.0;
  let skillActivated = false;
  const skill = ghostDef.activeSkill;
  if (skill && Math.random() < skill.chance) {
    let triggered = false;
    switch (skill.trigger) {
      case 'on_scare': triggered = true; break;
      case 'on_chain': triggered = chainCount > 0; break;
      case 'on_low_condition': triggered = (room.condition ?? 100) < 30; break;
      case 'on_high_fatigue': triggered = ghost.fatigue > 60; break;
      case 'on_first_visit': triggered = chainCount === 0; break;
    }
    if (triggered) {
      activeSkillBonus = 1 + skill.bonus;
      skillActivated = true;
    }
  }

  let total = 0;
  for (const visitor of visitors) {
    if (visitor.fled) continue; // fled visitors don't contribute
    const visitorDef = VISITOR_TYPES[visitor.type];
    const visitorMultiplier = visitorDef.rewardMultiplier / visitorDef.resistanceMultiplier;
    const sp = ghostDef.baseFear
      * upgradeMultiplier
      * affinityMultiplier
      * levelMultiplier
      * fatigueMultiplier
      * chainBonus
      * prestigeBonus
      * trainingBonus
      * propBonus
      * roomGhostSynergy
      * roomThemeBonus
      * ghostThemeBonus
      * preferredThemePairBonus
      * adMultiplier
      * snackBarBonus
      * visitorMultiplier
      * timePhaseMult
      * activeSkillBonus;
    total += sp;
  }

  return { sp: Math.max(1, Math.round(total)), skillActivated };
}

// ── Visitor Group Factory ──────────────────────────────────

let _groupCounter = 0;

/**
 * Pick a visitor type based on reputation and time phase.
 * Higher reputation = more interesting visitor types.
 * Night = more brave/thrill-seekers. Rain = more couples/cowards.
 */
function pickVisitorType(state: GameStateData, isVip: boolean): VisitorTypeId {
  if (isVip) {
    // VIP groups have more valuable visitors
    const vipPool: VisitorTypeId[] = ['brave', 'youtuber', 'thrill_seeker', 'couple'];
    return vipPool[Math.floor(Math.random() * vipPool.length)];
  }

  const rep = state.reputation;
  const phase = state.timePhase;

  // Build weighted pool based on reputation and time phase
  type WeightedType = { type: VisitorTypeId; weight: number };
  const pool: WeightedType[] = [
    { type: 'normal', weight: 30 },
    { type: 'scaredy_kid', weight: 15 },
    { type: 'coward', weight: 10 },
  ];

  // Unlock more interesting types at higher reputation
  if (rep >= 20) pool.push({ type: 'couple', weight: 12 });
  if (rep >= 35) pool.push({ type: 'brave', weight: 10 });
  if (rep >= 50) pool.push({ type: 'thrill_seeker', weight: 8 });
  if (rep >= 65) pool.push({ type: 'youtuber', weight: 5 });
  if (rep >= 80) pool.push({ type: 'exorcist', weight: 3 });

  // Time phase adjustments
  if (phase === 'night') {
    for (const entry of pool) {
      if (entry.type === 'brave' || entry.type === 'thrill_seeker') entry.weight *= 1.5;
      if (entry.type === 'scaredy_kid' || entry.type === 'coward') entry.weight *= 0.6;
    }
  } else if (phase === 'rain') {
    for (const entry of pool) {
      if (entry.type === 'couple') entry.weight *= 2.0;
      if (entry.type === 'coward') entry.weight *= 1.5;
      if (entry.type === 'normal') entry.weight *= 1.3;
    }
  }

  const totalWeight = pool.reduce((sum, e) => sum + e.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const entry of pool) {
    roll -= entry.weight;
    if (roll <= 0) return entry.type;
  }
  return 'normal';
}

function createVisitor(type: VisitorTypeId, groupIdx: number): Visitor {
  const visitorDef = VISITOR_TYPES[type];
  // Randomize personal attributes around the base (+-20%)
  const variance = () => 0.8 + Math.random() * 0.4;

  return {
    id: `v-${_groupCounter}-${groupIdx}`,
    type,
    fearLevel: visitorDef.initialFear,
    fearTolerance: Math.round(visitorDef.fearTolerance * variance()),
    excitement: visitorDef.baseExcitement,
    nausea: 0,
    satisfaction: 0.5, // start neutral
    budget: Math.round(visitorDef.baseBudget * variance()),
    patience: Math.round(visitorDef.basePatience * variance()),
    fled: false,
    currentReaction: null,
    reactionSetAt: 0,
  };
}

function createVisitorGroup(state: GameStateData, isVip: boolean = false): VisitorGroup {
  // Always spawn exactly 1 visitor per group
  const type = pickVisitorType(state, isVip);
  const visitors: Visitor[] = [createVisitor(type, 0)];

  _groupCounter++;
  return {
    id: `g-${_groupCounter}`,
    visitors,
    currentFloorIndex: 0,
    currentRoomIndex: 0,
    timeAtRoomMs: 0,
    scareChainCount: 0,
    done: false,
    totalSpEarned: 0,
    roomsVisitedWithGhost: 0,
    isVip,
    phase: 'entering' as const,
    phaseStartedAt: Date.now(),
  };
}

// ── Time Phase System ──────────────────────────────────────

const TIME_PHASE_DURATION_MS = 120000; // 2 minutes per phase

function timePhaseSystem(state: GameStateData, _deltaMs: number): GameStateData {
  if (!state.isOpen) return state;

  const now = Date.now();
  if (state.timePhaseStartAt === 0) {
    return { ...state, timePhaseStartAt: now };
  }

  const elapsed = now - state.timePhaseStartAt;
  if (elapsed < TIME_PHASE_DURATION_MS) return state;

  // Cycle: day -> night -> rain -> day...
  const phases: Array<'day' | 'night' | 'rain'> = ['day', 'night', 'rain'];
  const currentIdx = phases.indexOf(state.timePhase);
  const nextPhase = phases[(currentIdx + 1) % phases.length];

  return {
    ...state,
    timePhase: nextPhase,
    timePhaseStartAt: now,
  };
}

// ── Visitor Movement System ────────────────────────────────

/** Tracking review generation across ticks */
let _pendingReview: {
  stars: 1|2|3|4|5;
  textKey: string;
  ghostNameKey: string | null;
  visitorType?: string;
  floorLevel?: number;
  satisfaction?: number;
} | null = null;

export function consumePendingReview(): typeof _pendingReview {
  const r = _pendingReview;
  _pendingReview = null;
  return r;
}

/**
 * Update visitor psychological state after a room encounter.
 * This is the core roller-coaster-tycoon logic.
 */
function updateVisitorPsychology(
  visitor: Visitor,
  scarePower: number,
  hadGhost: boolean,
  statSatisfactionBonus: number = 0,
): Visitor {
  if (visitor.fled) return visitor;

  const visitorDef = VISITOR_TYPES[visitor.type];
  let newFear = visitor.fearLevel + scarePower;
  let newPatience = visitor.patience;
  let newExcitement = visitor.excitement;
  let newNausea = visitor.nausea;

  if (!hadGhost) {
    // Empty room: patience decreases, excitement drops
    newPatience = Math.max(0, newPatience - 15);
    newExcitement = Math.max(0, newExcitement - 5);
  } else {
    // Room with ghost: calculate psychological impact
    newExcitement = Math.min(100, newExcitement + scarePower * 0.3);
    newNausea = calcNausea(newFear, visitor.fearTolerance, visitorDef.nauseaRate);
  }

  // Running satisfaction from bell curve, blended with stat-preference match
  // statSatisfactionBonus is 0-1 range from calcStatSatisfaction
  const baseSatisfaction = calcSatisfactionCurve(newFear, visitor.fearTolerance);
  // Blend: 70% fear curve, 30% stat preference match
  const satisfaction = hadGhost && statSatisfactionBonus > 0
    ? Math.min(1, baseSatisfaction * 0.7 + statSatisfactionBonus * 0.3)
    : baseSatisfaction;

  // Check for flee
  const fled = shouldVisitorFlee(satisfaction, newNausea, newPatience, visitorDef.fleeThreshold);

  // Determine reaction based on satisfaction (0-1 scale, convert to 0-100 range)
  const satPct = satisfaction * 100;
  let reaction: VisitorReaction = null;
  if (hadGhost) {
    if (satPct > 80) reaction = 'excited';
    else if (satPct > 50) reaction = 'scared';
    else if (satPct > 30) reaction = 'normal';
    else if (satPct > 10) reaction = 'bored';
    else reaction = 'angry';
  }

  return {
    ...visitor,
    fearLevel: Math.min(100, newFear),
    patience: newPatience,
    excitement: newExcitement,
    nausea: newNausea,
    satisfaction,
    fled,
    currentReaction: reaction,
    reactionSetAt: reaction ? Date.now() : visitor.reactionSetAt,
  };
}

// ── Phase Duration Constants (ms) ──────────────────────────
const PHASE_DURATIONS: Record<VisitorPhase, number> = {
  entering: 2500,  // visitors walk into room
  fog: 2000,       // fog builds up
  scare: 2500,     // ghost pops out, reaction shown
  leaving: 1500,   // visitors walk out, advance to next floor
};

/** Get the next phase in the sequence, or null if cycle is done */
function nextPhase(phase: VisitorPhase): VisitorPhase | null {
  switch (phase) {
    case 'entering': return 'fog';
    case 'fog': return 'scare';
    case 'scare': return 'leaving';
    case 'leaving': return null; // cycle complete, advance floor
  }
}

function processVisitorSystem(state: GameStateData, _deltaMs: number): GameStateData {
  const now = Date.now();

  // Spawn visitor group if none -- only when house is OPEN
  if (!state.visitorGroup || state.visitorGroup.done) {
    if (!state.isOpen) return state; // Closed: no visitors

    // During evening phase, lobbyQueueSystem handles spawning via queue
    // processVisitorSystem only handles group movement through the building
    if (state.dayPhase === 'evening') {
      return state; // Queue system promotes visitors
    }

    // Floor 1 blocked check: if floor 1 is under repair/collapsed/cleaning, block all entry
    const floor1 = state.building[0];
    if (floor1 && floor1.room && (floor1.room.collapsed || floor1.room.underRepair || floor1.room.underCleaning)) {
      // Reduce reputation while entry is blocked
      const newRep = Math.max(0, state.reputation - 0.05);
      if (newRep !== state.reputation) {
        return { ...state, reputation: newRep };
      }
      return state; // No spawning while floor 1 is blocked
    }

    if (now >= state.nextVisitorSpawnAt) {
      // Only spawn if building has at least 1 room built
      const hasRoom = state.building.some((f) => f.room !== null);
      if (hasRoom) {
        // Higher ticket price = fewer visitors (longer spawn interval)
        const priceMultiplier = 1 + (state.ticketPrice - 10) * 0.03;
        // Reputation affects spawn rate: high rep = more visitors
        const repMultiplier = Math.max(0.5, 1.5 - state.reputation / 100);
        // Time phase affects spawn: night = fewer, rain = more
        const phaseMultiplier = state.timePhase === 'night' ? 1.3
          : state.timePhase === 'rain' ? 0.7
          : 1.0;
        // Promoter bonus: more visitors
        const promoterBonus = calcPromoterBonus(state);
        const promoterMultiplier = Math.max(0.3, 1 - promoterBonus);
        const adjustedSpawnInterval = Math.round(
          CONFIG.visitorSpawnIntervalMs
          * Math.max(0.3, priceMultiplier)
          * repMultiplier
          * phaseMultiplier
          * promoterMultiplier,
        );
        // Consume some recommendation pool
        const newRecPool = Math.max(0, state.recommendationPool - 1);
        return {
          ...state,
          visitorGroup: createVisitorGroup(state),
          nextVisitorSpawnAt: now + adjustedSpawnInterval,
          todayVisitors: state.todayVisitors + 1,
          recommendationPool: newRecPool,
        };
      }
      // No rooms: schedule next attempt
      return { ...state, nextVisitorSpawnAt: now + CONFIG.visitorSpawnIntervalMs };
    }
    return state;
  }

  const group = state.visitorGroup;

  // Check elapsed time in current phase
  const phaseElapsed = now - group.phaseStartedAt;
  const phaseDuration = PHASE_DURATIONS[group.phase];

  // Phase not yet complete: just accumulate time
  if (phaseElapsed < phaseDuration) {
    return state;
  }

  // Check if all visitors have fled
  const allFled = group.visitors.every((v) => v.fled);
  if (allFled) {
    // Everyone fled: generate bad review immediately
    const newGroup = { ...group, done: true };

    // Medic staff: neutralize bad review from 1-star to 2-star
    const medicPresent = hasMedicStaff(state);
    const stars = (medicPresent ? 2 : 1) as 1 | 2;
    const reviewTemplate = pickReviewTemplate(stars);
    let ghostNameKey: string | null = null;
    if (reviewTemplate.requiresGhost && state.ghosts.length > 0) {
      const randomGhost = state.ghosts[Math.floor(Math.random() * state.ghosts.length)];
      const ghostDef = GHOST_DEFS[randomGhost.characterId];
      ghostNameKey = ghostDef?.nameKey ?? null;
    }

    const reviewFame = fameFromReview(stars);
    // Youtuber amplification on bad reviews
    const hasYoutuber = group.visitors.some((v) => v.type === 'youtuber');
    const influenceMultiplier = hasYoutuber ? 2.5 : 1.0;

    _pendingReview = {
      stars,
      textKey: reviewTemplate.textKey,
      ghostNameKey,
      visitorType: group.visitors[0]?.type,
      floorLevel: group.currentFloorIndex + 1,
      satisfaction: 0,
    };

    return {
      ...state,
      visitorGroup: newGroup,
      fame: Math.max(0, state.fame + Math.round(reviewFame * influenceMultiplier)),
      reputation: Math.max(0, state.reputation - 3),
      todayFleeCount: state.todayFleeCount + group.visitors.length,
    };
  }

  // Current floor context
  const currentFloor = state.building[group.currentFloorIndex];
  if (!currentFloor) {
    return { ...state, visitorGroup: { ...group, done: true } };
  }

  const currentRoom = (group.currentRoomIndex === 0 && currentFloor.room) ? currentFloor.room : null;

  // Skip collapsed/repairing/cleaning rooms immediately (fast-forward all phases)
  if (currentRoom && (currentRoom.collapsed || currentRoom.underRepair || currentRoom.underCleaning)) {
    let skipGroup: VisitorGroup = {
      ...group,
      visitors: group.visitors.map((v) => updateVisitorPsychology(v, 0, false)),
    };
    const skipNextFloorIndex = group.currentFloorIndex + 1;
    if (skipNextFloorIndex < state.building.length) {
      skipGroup = { ...skipGroup, currentFloorIndex: skipNextFloorIndex, currentRoomIndex: 0, phase: 'entering' as const, phaseStartedAt: now };
    } else {
      skipGroup = { ...skipGroup, done: true };
    }
    return { ...state, visitorGroup: skipGroup };
  }

  // === Phase transition logic ===
  const currentPhase = group.phase;
  const hasGhost = !!(currentRoom && currentRoom.assignedGhostId);
  const hasEffects = !!(currentRoom && currentRoom.props && currentRoom.props.length > 0);

  // No ghost AND no effects → skip fog/scare, just walk through
  if (!hasGhost && !hasEffects) {
    if (currentPhase === 'entering') {
      // Go straight to leaving (walk through without stopping)
      return {
        ...state,
        visitorGroup: { ...group, phase: 'leaving' as VisitorPhase, phaseStartedAt: now },
      };
    }
    if (currentPhase === 'leaving') {
      return processFloorAdvance(state, group, now);
    }
  }

  // No ghost but has effects → skip scare phase only
  if (!hasGhost && hasEffects) {
    if (currentPhase === 'fog') {
      // Skip scare, go to leaving with bored reaction
      const boredVisitors = group.visitors.map((v) => ({
        ...v,
        currentReaction: 'bored' as VisitorReaction,
        reactionSetAt: now,
      }));
      return {
        ...state,
        visitorGroup: { ...group, visitors: boredVisitors, phase: 'leaving' as VisitorPhase, phaseStartedAt: now },
      };
    }
  }

  const next = nextPhase(currentPhase);

  // ── SCARE phase just completed: process scare + degradation ──
  if (currentPhase === 'scare') {
    return processScareAndTransition(state, group, currentFloor, currentRoom, now);
  }

  // ── LEAVING phase just completed: advance to next floor ──
  if (next === null) {
    return processFloorAdvance(state, group, now);
  }

  // ── FOG → SCARE: set reactions NOW so they show during scare phase ──
  if (currentPhase === 'fog' && next === 'scare') {
    const currentFloorForReaction = state.building[group.currentFloorIndex];
    const currentRoomForReaction = currentFloorForReaction?.room ?? null;
    const ghostAssigned = !!(currentRoomForReaction?.assignedGhostId);
    const updatedVisitors = group.visitors.map((v) => {
      if (v.fled) return v;
      if (ghostAssigned) {
        // Pre-set reaction based on visitor's current satisfaction
        const satPct = (v.satisfaction ?? 0.5) * 100;
        let reaction: VisitorReaction = null;
        if (satPct > 80) reaction = 'excited';
        else if (satPct > 50) reaction = 'scared';
        else if (satPct > 30) reaction = 'normal';
        else if (satPct > 10) reaction = 'bored';
        else reaction = 'angry';
        // Default to scared if no satisfaction data yet
        if (!reaction || reaction === 'normal') reaction = 'scared';
        return { ...v, currentReaction: reaction, reactionSetAt: now };
      }
      return { ...v, currentReaction: 'bored' as VisitorReaction, reactionSetAt: now };
    });
    return {
      ...state,
      visitorGroup: {
        ...group,
        visitors: updatedVisitors,
        phase: next,
        phaseStartedAt: now,
      },
    };
  }

  // ── ENTERING phase completed: just transition to next phase ──
  return {
    ...state,
    visitorGroup: {
      ...group,
      phase: next,
      phaseStartedAt: now,
    },
  };
}

/**
 * Process the scare calculation and room degradation when the scare phase ends.
 * Then transition to the leaving phase.
 */
function processScareAndTransition(
  state: GameStateData,
  group: VisitorGroup,
  currentFloor: GameStateData['building'][0],
  currentRoom: GameStateData['building'][0]['room'],
  now: number,
): GameStateData {
  let newState = state;
  let newGroup: VisitorGroup = { ...group, phase: 'leaving' as const, phaseStartedAt: now };
  const newPopups: ScarePopup[] = [];

  let scarePower = 0;
  const hadGhost = !!(currentRoom && currentRoom.assignedGhostId);

  // Apply condition/hygiene degradation on visit
  if (currentRoom) {
    const condUpgrade = currentRoom.conditionUpgrade ?? 0;
    const hygUpgrade = currentRoom.hygieneUpgrade ?? 0;

    const conditionLoss = 2 * (1 - condUpgrade * 0.05);
    const hygieneLoss = 3 * (1 - hygUpgrade * 0.05);

    let newCondition = Math.max(0, currentRoom.condition - conditionLoss);
    let newHygiene = Math.max(0, currentRoom.hygiene - hygieneLoss);

    // Lv20 bonus: condition min 50
    if (condUpgrade >= 20) newCondition = Math.max(50, newCondition);
    // Lv20 bonus: hygiene auto recovery
    if (hygUpgrade >= 20) newHygiene = Math.min(100, newHygiene + 0.5);

    let newCollapsed = currentRoom.collapsed ?? false;
    if (newCondition <= 0 && !newCollapsed) {
      newCollapsed = true;
    }

    // Hygiene 0: visitors flee immediately
    if (newHygiene <= 0) {
      newGroup = {
        ...newGroup,
        visitors: newGroup.visitors.map((v) =>
          v.fled ? v : { ...v, currentReaction: 'angry' as const, reactionSetAt: now, fled: true },
        ),
      };
    }

    // Update the room in state
    newState = {
      ...newState,
      building: newState.building.map((f) =>
        f.level === currentFloor.level && f.room
          ? {
              ...f,
              room: {
                ...f.room,
                condition: newCondition,
                hygiene: newHygiene,
                collapsed: newCollapsed,
              },
            }
          : f,
      ),
    };
  }

  // Perform scare if room exists and has ghost
  if (currentRoom && currentRoom.assignedGhostId) {
    const scareResult = calcSpEarned(
      state,
      currentRoom.id,
      currentRoom.assignedGhostId,
      group.visitors,
      group.scareChainCount,
    );
    const spEarned = scareResult.sp;

    if (spEarned > 0) {
      const ticketMultiplier = state.ticketPrice / 10;
      const storyMultiplier = 1 + (state.storyBonus ?? 0) / 100;
      const adjustedSp = Math.max(1, Math.round(spEarned * ticketMultiplier * storyMultiplier));
      scarePower = adjustedSp / Math.max(1, group.visitors.filter((v) => !v.fled).length);

      newGroup = {
        ...newGroup,
        scareChainCount: group.scareChainCount + 1,
        totalSpEarned: group.totalSpEarned + adjustedSp,
        roomsVisitedWithGhost: group.roomsVisitedWithGhost + 1,
      };

      // Add fatigue to ghost
      newState = {
        ...newState,
        sp: newState.sp + adjustedSp,
        totalSpEarned: newState.totalSpEarned + adjustedSp,
        todayEarnings: newState.todayEarnings + adjustedSp,
        ghosts: newState.ghosts.map((g) =>
          g.id === currentRoom.assignedGhostId
            ? {
                ...g,
                fatigue: Math.min(100, g.fatigue + CONFIG.fatiguePerScare),
                experience: g.experience + CONFIG.ghostExpPerScare,
              }
            : g,
        ),
      };

      // Add popup
      newPopups.push({
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        ghostId: currentRoom.assignedGhostId,
        spEarned,
        floorLevel: currentFloor.level,
        expiresAt: now + 2000,
        skillActivated: scareResult.skillActivated,
      });
    }
  }

  // Update visitor psychology (with stat-preference matching)
  newGroup = {
    ...newGroup,
    visitors: newGroup.visitors.map((v) => {
      let statBonus = 0;
      if (currentRoom && hadGhost) {
        const roomStats = calcRoomStats(currentRoom, newState);
        const visitorDef = VISITOR_TYPES[v.type];
        statBonus = calcStatSatisfaction(roomStats, visitorDef.statPreference);
      }
      // Security staff bonus: +satisfaction
      const secBonus = calcSecurityBonus(newState);
      return updateVisitorPsychology(v, scarePower, hadGhost, statBonus + secBonus);
    }),
  };

  // Count how many fled this tick
  const newFleeCount = newGroup.visitors.filter((v) => v.fled).length
    - group.visitors.filter((v) => v.fled).length;

  if (newFleeCount > 0) {
    newState = {
      ...newState,
      todayFleeCount: newState.todayFleeCount + newFleeCount,
    };
  }

  // Expire old popups + add new
  const freshPopups = [
    ...newState.scarePopups.filter((p) => p.expiresAt > now),
    ...newPopups,
  ];

  return {
    ...newState,
    visitorGroup: newGroup,
    scarePopups: freshPopups,
  };
}

/**
 * Advance to next floor after leaving phase completes, or finish the visit.
 */
function processFloorAdvance(
  state: GameStateData,
  group: VisitorGroup,
  now: number,
): GameStateData {
  let newState = state;
  let newGroup = { ...group };

  const nextFloorIndex = group.currentFloorIndex + 1;
  if (nextFloorIndex < state.building.length) {
    // Move to next floor, start entering phase
    newGroup = {
      ...newGroup,
      currentFloorIndex: nextFloorIndex,
      currentRoomIndex: 0,
      phase: 'entering' as const,
      phaseStartedAt: now,
    };
  } else {
    // All floors visited: generate review based on satisfaction
    newGroup = { ...newGroup, done: true };

    // Calculate average satisfaction across non-fled visitors
    const activeVisitors = newGroup.visitors.filter((v) => !v.fled);
    const avgSatisfaction = activeVisitors.length > 0
      ? activeVisitors.reduce((sum, v) => sum + v.satisfaction, 0) / activeVisitors.length
      : 0;
    const avgExcitement = activeVisitors.length > 0
      ? activeVisitors.reduce((sum, v) => sum + v.excitement, 0) / activeVisitors.length
      : 0;

    // Star rating based on satisfaction curve + SP earned
    const satisfactionStars = avgSatisfaction >= 0.85 ? 5
      : avgSatisfaction >= 0.65 ? 4
      : avgSatisfaction >= 0.45 ? 3
      : avgSatisfaction >= 0.25 ? 2
      : 1;
    const spStars = calcReviewStars(newGroup.totalSpEarned, newGroup.roomsVisitedWithGhost);
    // Blend: 60% satisfaction-based, 40% SP-based
    const blendedScore = satisfactionStars * 0.6 + spStars * 0.4;
    const stars = Math.max(1, Math.min(5, Math.round(blendedScore))) as 1|2|3|4|5;

    const reviewTemplate = pickReviewTemplate(stars);

    // Get a random ghost name for the review
    let ghostNameKey: string | null = null;
    if (reviewTemplate.requiresGhost && newState.ghosts.length > 0) {
      const randomGhost = newState.ghosts[Math.floor(Math.random() * newState.ghosts.length)];
      const ghostDef = GHOST_DEFS[randomGhost.characterId];
      ghostNameKey = ghostDef?.nameKey ?? null;
    }

    // Youtuber influence
    const hasYoutuber = newGroup.visitors.some((v) => v.type === 'youtuber');
    const influenceMultiplier = hasYoutuber ? 2.5 : 1.0;

    const reviewFame = fameFromReview(stars);
    const baseFame = CONFIG.famePerFullRun;

    // Facility: photo_booth gives extra SP on group completion
    const photoBooth = newState.facilities.find((f) => f.facilityId === 'photo_booth');
    const photoBoothSp = photoBooth
      ? FACILITY_DEFS.photo_booth.spPerTick * photoBooth.level
      : 0;

    // Facility: gift_shop gives passive SP on group completion
    const giftShop = newState.facilities.find((f) => f.facilityId === 'gift_shop');
    const avgBudget = activeVisitors.length > 0
      ? activeVisitors.reduce((sum, v) => sum + v.budget, 0) / activeVisitors.length
      : 0;
    const giftShopSp = giftShop
      ? Math.round(FACILITY_DEFS.gift_shop.spPerTick * giftShop.level * (1 + avgBudget / 50))
      : 0;

    const facilitySp = photoBoothSp + giftShopSp;

    // VIP bonus
    const vipFameBonus = group.isVip ? 3 : 0;

    // Recommendation: satisfied visitors bring friends
    const recommendationGain = stars >= 4 ? Math.round(avgExcitement / 20) : 0;
    // Reputation update based on review
    const repChange = stars >= 4 ? 2 : stars >= 3 ? 0 : stars >= 2 ? -1 : -3;

    newState = {
      ...newState,
      fame: Math.max(0, newState.fame + Math.round((baseFame + reviewFame + vipFameBonus) * influenceMultiplier)),
      sp: newState.sp + facilitySp,
      totalSpEarned: newState.totalSpEarned + facilitySp,
      reputation: Math.max(0, Math.min(100, newState.reputation + repChange)),
      recommendationPool: newState.recommendationPool + recommendationGain,
    };

    // Queue the review for the command dispatch (handled in main.ts)
    _pendingReview = {
      stars,
      textKey: reviewTemplate.textKey,
      ghostNameKey,
      visitorType: newGroup.visitors[0]?.type,
      floorLevel: newGroup.currentFloorIndex + 1,
      satisfaction: avgSatisfaction,
    };
  }

  // Expire old popups
  const freshPopups = newState.scarePopups.filter((p) => p.expiresAt > now);

  return {
    ...newState,
    visitorGroup: newGroup,
    scarePopups: freshPopups,
  };
}

// ── Fatigue Recovery System ────────────────────────────────

function fatigueRecoverySystem(state: GameStateData, deltaMs: number): GameStateData {
  const newAccum = state.fatigueTickAccumMs + deltaMs;
  const HOUR_MS = 3600000;
  const hoursElapsed = Math.floor(newAccum / HOUR_MS);

  if (hoursElapsed === 0) {
    return { ...state, fatigueTickAccumMs: newAccum };
  }

  const recoveryPerHour = CONFIG.fatigueRecoveryPerHour;
  const totalRecovery = hoursElapsed * recoveryPerHour;

  return {
    ...state,
    fatigueTickAccumMs: newAccum % HOUR_MS,
    ghosts: state.ghosts.map((g) => ({
      ...g,
      fatigue: Math.max(0, g.fatigue - totalRecovery),
    })),
  };
}

// ── Popup Cleanup System ───────────────────────────────────

function popupCleanupSystem(state: GameStateData, _deltaMs: number): GameStateData {
  const now = Date.now();
  const fresh = state.scarePopups.filter((p) => p.expiresAt > now);
  if (fresh.length === state.scarePopups.length) return state;
  return { ...state, scarePopups: fresh };
}

// ── Ad Boost Cleanup System ────────────────────────────────

function adBoostCleanupSystem(state: GameStateData, _deltaMs: number): GameStateData {
  const now = Date.now();
  const active = state.adBoosts.filter((b) => b.expiresAt > now);
  if (active.length === state.adBoosts.length) return state;
  return { ...state, adBoosts: active };
}

// ── Repair / Cleaning Completion System ────────────────────

function repairCleaningSystem(state: GameStateData, _deltaMs: number): GameStateData {
  const now = Date.now();
  let changed = false;
  const newBuilding = state.building.map((floor) => {
    if (!floor.room) return floor;
    const room = floor.room;
    let updatedRoom = room;

    // Check repair completion
    if (room.underRepair && room.repairEndsAt > 0 && now >= room.repairEndsAt) {
      updatedRoom = {
        ...updatedRoom,
        underRepair: false,
        repairEndsAt: 0,
        condition: 100,
        collapsed: false,
      };
      changed = true;
    }

    // Check cleaning completion
    if (room.underCleaning && room.cleaningEndsAt > 0 && now >= room.cleaningEndsAt) {
      updatedRoom = {
        ...updatedRoom,
        underCleaning: false,
        cleaningEndsAt: 0,
        hygiene: 100,
      };
      changed = true;
    }

    if (updatedRoom !== room) {
      return { ...floor, room: updatedRoom };
    }
    return floor;
  });

  if (!changed) return state;
  return { ...state, building: newBuilding };
}

// ── Main Tick ──────────────────────────────────────────────

// ── Daily Milestones Progress Update ───────────────────────

function updateMilestonesSystem(state: GameStateData): GameStateData {
  if (state.dailyMilestones.length === 0) return state;

  const activeRooms = state.building.filter((f) => f.room !== null).length;
  const deployedGhosts = state.ghosts.filter((g) => g.assignedRoomId !== null).length;

  let changed = false;
  const updated = state.dailyMilestones.map((m) => {
    if (m.collected) return m;
    let current = m.current;
    let completed = m.completed;

    if (m.nameKey.startsWith('milestone_visitors_')) {
      current = state.todayVisitors;
      completed = current >= m.target;
    } else if (m.nameKey.startsWith('milestone_earnings_')) {
      current = state.todayEarnings;
      completed = current >= m.target;
    } else if (m.nameKey === 'milestone_flee_0') {
      current = state.todayFleeCount;
      // For flee_0, target is 0 - completed if zero fleers AND at least 1 visitor
      completed = current === 0 && state.todayVisitors > 0;
    } else if (m.nameKey === 'milestone_flee_1') {
      current = state.todayFleeCount;
      completed = current <= m.target && state.todayVisitors > 0;
    } else if (m.nameKey.startsWith('milestone_rooms_')) {
      current = activeRooms;
      completed = current >= m.target;
    } else if (m.nameKey.startsWith('milestone_ghosts_')) {
      current = deployedGhosts;
      completed = current >= m.target;
    }

    if (current !== m.current || completed !== m.completed) {
      changed = true;
      return { ...m, current, completed };
    }
    return m;
  });

  if (!changed) return state;
  return { ...state, dailyMilestones: updated };
}

// ── Day Phase System ───────────────────────────────────────

const DAY_PHASE_DURATION_MS = 60000;     // 60 seconds for day phase
const EVENING_PHASE_DURATION_MS = 120000; // 120 seconds for evening phase

// ── In-Game Clock ──────────────────────────────────────────
// Maps real-time phase progress to in-game hours
// Day phase: 9AM → 5PM (preparation, 60s = 8 hours)
// Evening phase: 5PM → 12AM (business, 120s = 7 hours)
// Night phase: 12AM (summary)
const CLOCK_DAY_START_HOUR = 9;      // 9:00 AM
const CLOCK_DAY_END_HOUR = 17;       // 5:00 PM
const CLOCK_EVENING_START_HOUR = 17; // 5:00 PM
const CLOCK_EVENING_END_HOUR = 24;   // 12:00 AM (midnight)

export interface InGameTime {
  hour: number;   // 0-23
  minute: number;  // 0-59
  progress: number; // 0-1 within current phase
}

/** Get the current in-game clock time based on phase and elapsed time */
export function getInGameTime(state: GameStateData): InGameTime {
  if (!state.dayPhaseStartedAt) {
    return { hour: CLOCK_DAY_START_HOUR, minute: 0, progress: 0 };
  }

  const elapsed = Date.now() - state.dayPhaseStartedAt;
  const speed = state.gameSpeed || 1;

  if (state.dayPhase === 'day') {
    const progress = Math.min(1, elapsed / DAY_PHASE_DURATION_MS);
    const totalMinutes = (CLOCK_DAY_END_HOUR - CLOCK_DAY_START_HOUR) * 60;
    const currentMinutes = Math.floor(progress * totalMinutes);
    const hour = CLOCK_DAY_START_HOUR + Math.floor(currentMinutes / 60);
    const minute = currentMinutes % 60;
    return { hour, minute, progress };
  }

  if (state.dayPhase === 'evening') {
    const effectiveDuration = EVENING_PHASE_DURATION_MS / speed;
    const progress = Math.min(1, elapsed / effectiveDuration);
    const totalMinutes = (CLOCK_EVENING_END_HOUR - CLOCK_EVENING_START_HOUR) * 60;
    const currentMinutes = Math.floor(progress * totalMinutes);
    const hour = CLOCK_EVENING_START_HOUR + Math.floor(currentMinutes / 60);
    const minute = currentMinutes % 60;
    return { hour, minute, progress };
  }

  // Night
  return { hour: 0, minute: 0, progress: 1 };
}

/** Format in-game time as display string (e.g., "PM 5:00", "AM 12:00") */
export function formatInGameTime(time: InGameTime): string {
  const h = time.hour % 12 || 12;
  const period = time.hour < 12 ? 'AM' : 'PM';
  const m = String(time.minute).padStart(2, '0');
  return `${period} ${h}:${m}`;
}

/** Get time-of-day multiplier for visitor spawning (evening only) */
export function getTimeBasedSpawnMultiplier(time: InGameTime): number {
  if (time.hour < 17) return 1.0; // day phase, no visitors
  if (time.hour < 19) return 0.6; // 5-7PM: early, building up
  if (time.hour < 21) return 1.4; // 7-9PM: peak hours
  if (time.hour < 23) return 1.2; // 9-11PM: horror enthusiasts
  return 0.8;                      // 11PM-12AM: winding down
}

/** Track if we've already signaled night transition this cycle */
let _nightTransitionPending = false;

export function consumeNightTransition(): boolean {
  const pending = _nightTransitionPending;
  _nightTransitionPending = false;
  return pending;
}

/** Track if day phase auto-transition is pending */
let _dayAutoTransitionPending = false;

export function consumeDayAutoTransition(): boolean {
  const pending = _dayAutoTransitionPending;
  _dayAutoTransitionPending = false;
  return pending;
}

function dayPhaseSystem(state: GameStateData, _deltaMs: number): GameStateData {
  // Only process if dayPhaseStartedAt is set
  if (!state.dayPhaseStartedAt) return state;

  const now = Date.now();
  const elapsed = now - state.dayPhaseStartedAt;
  const speed = state.gameSpeed || 1;

  if (state.dayPhase === 'day') {
    // Day phase auto-transitions to a notification at 60s (but doesn't force-open)
    if (elapsed > DAY_PHASE_DURATION_MS) {
      _dayAutoTransitionPending = true;
    }
    return state;
  }

  if (state.dayPhase === 'evening') {
    // Evening duration is affected by game speed
    const effectiveDuration = EVENING_PHASE_DURATION_MS / speed;
    if (elapsed > effectiveDuration) {
      // Signal night transition (main.ts will handle the popup)
      _nightTransitionPending = true;
      return state;
    }
    return state;
  }

  // Night phase: just wait for player to advance
  return state;
}

/** Get the remaining seconds in the current day phase */
export function getDayPhaseRemaining(state: GameStateData): number {
  if (!state.dayPhaseStartedAt) return 0;
  const elapsed = Date.now() - state.dayPhaseStartedAt;
  const speed = state.gameSpeed || 1;

  if (state.dayPhase === 'day') {
    return Math.max(0, Math.ceil((DAY_PHASE_DURATION_MS - elapsed) / 1000));
  }
  if (state.dayPhase === 'evening') {
    const effectiveDuration = EVENING_PHASE_DURATION_MS / speed;
    return Math.max(0, Math.ceil((effectiveDuration - elapsed) / 1000));
  }
  return 0;
}

// ── Ghost Staff System ─────────────────────────────────────

function ghostStaffSystem(state: GameStateData, deltaMs: number): GameStateData {
  const staffList = state.ghostStaff;
  if (!staffList || staffList.length === 0) return state;
  if (!state.isOpen) return state;

  let newState = state;

  for (const staff of staffList) {
    switch (staff.type) {
      case 'repairman': {
        // Auto-repair: find lowest condition room and slowly repair it
        let worstFloorIdx = -1;
        let worstCondition = 101;
        for (let i = 0; i < newState.building.length; i++) {
          const room = newState.building[i].room;
          if (room && !room.underRepair && !room.underCleaning && room.condition < worstCondition && room.condition < 80) {
            worstCondition = room.condition;
            worstFloorIdx = i;
          }
        }
        if (worstFloorIdx >= 0) {
          const floor = newState.building[worstFloorIdx];
          if (floor.room) {
            const repairPerSec = 0.5 * staff.level;
            const repairAmount = repairPerSec * (deltaMs / 1000);
            const newCondition = Math.min(100, floor.room.condition + repairAmount);
            newState = {
              ...newState,
              building: newState.building.map((f, idx) =>
                idx === worstFloorIdx && f.room
                  ? { ...f, room: { ...f.room, condition: newCondition, collapsed: newCondition > 0 ? false : f.room.collapsed } }
                  : f,
              ),
            };
          }
        }
        break;
      }
      case 'janitor': {
        // Auto-clean: find lowest hygiene room and slowly clean it
        let worstFloorIdx = -1;
        let worstHygiene = 101;
        for (let i = 0; i < newState.building.length; i++) {
          const room = newState.building[i].room;
          if (room && !room.underRepair && !room.underCleaning && room.hygiene < worstHygiene && room.hygiene < 80) {
            worstHygiene = room.hygiene;
            worstFloorIdx = i;
          }
        }
        if (worstFloorIdx >= 0) {
          const floor = newState.building[worstFloorIdx];
          if (floor.room) {
            const cleanPerSec = 0.5 * staff.level;
            const cleanAmount = cleanPerSec * (deltaMs / 1000);
            const newHygiene = Math.min(100, floor.room.hygiene + cleanAmount);
            newState = {
              ...newState,
              building: newState.building.map((f, idx) =>
                idx === worstFloorIdx && f.room
                  ? { ...f, room: { ...f.room, hygiene: newHygiene } }
                  : f,
              ),
            };
          }
        }
        break;
      }
      // promoter, medic, security: passive bonuses applied elsewhere
      default:
        break;
    }
  }

  return newState;
}

/** Calculate promoter bonus: +20% visitor spawn per promoter */
export function calcPromoterBonus(state: GameStateData): number {
  const promoters = (state.ghostStaff || []).filter((s) => s.type === 'promoter');
  return promoters.reduce((bonus, p) => bonus + 0.20 * p.level, 0);
}

/** Calculate security bonus: +10% satisfaction per security */
export function calcSecurityBonus(state: GameStateData): number {
  const guards = (state.ghostStaff || []).filter((s) => s.type === 'security');
  return guards.reduce((bonus, g) => bonus + 0.10 * g.level, 0);
}

/** Check if medic can neutralize a bad review */
export function hasMedicStaff(state: GameStateData): boolean {
  return (state.ghostStaff || []).some((s) => s.type === 'medic');
}

// ── Lobby Queue System ─────────────────────────────────────

/** Interval (ms) between queue ticks (patience decay) */
const QUEUE_TICK_INTERVAL_MS = 1000;
/** Interval (ms) between spawning new visitors into queue */
const QUEUE_SPAWN_INTERVAL_MS = 6000;

/**
 * Aggregated unique effects from all installed lobby facilities.
 */
export interface LobbyFacilityEffects {
  /** Ticket booth: reduce spawn interval by this fraction per level */
  ticketSpeedBonus: number;
  /** Waiting lounge: extra queue capacity */
  maxQueueBonus: number;
  /** VIP lounge: VIP appearance rate multiplier */
  vipRateMultiplier: number;
  /** Poster wall: base satisfaction bonus (0-1) */
  satisfactionBonus: number;
  /** Snack cart: patience recovery for visitors below threshold */
  emergencyPatienceRecovery: number;
  /** Souvenir booth: revisit rate bonus (applied at day end) */
  revisitRateBonus: number;
  /** Photo zone: daily reputation gain */
  reputationPerDay: number;
  /** TV monitor: anticipation bonus (applied to excitement) */
  anticipationBonus: number;
  /** Costume rental: fun stat bonus applied to room visits */
  funBonus: number;
  /** Fortune booth: chance of random bonus SP */
  randomSpBonusChance: number;
}

/**
 * Calculate aggregated unique effects from all lobby building facilities.
 */
export function calcLobbyFacilityEffects(state: GameStateData): LobbyFacilityEffects {
  const effects: LobbyFacilityEffects = {
    ticketSpeedBonus: 0,
    maxQueueBonus: 0,
    vipRateMultiplier: 1,
    satisfactionBonus: 0,
    emergencyPatienceRecovery: 0,
    revisitRateBonus: 0,
    reputationPerDay: 0,
    anticipationBonus: 0,
    funBonus: 0,
    randomSpBonusChance: 0,
  };

  for (const floor of (state.lobbyBuilding || [])) {
    if (!floor.facilityType) continue;
    const def = LOBBY_FACILITY_DEFS[floor.facilityType];
    if (!def) continue;

    const lvl = Math.max(1, floor.facilityLevel);

    switch (def.uniqueEffect.type) {
      case 'ticket_speed':
        // Per level: 15% faster entry interval
        effects.ticketSpeedBonus += def.uniqueEffect.value * lvl;
        break;
      case 'max_queue_up':
        // +5 per level
        effects.maxQueueBonus += def.uniqueEffect.value * lvl;
        break;
      case 'vip_rate_up':
        // Multiplier stacks multiplicatively per level
        effects.vipRateMultiplier *= def.uniqueEffect.value * lvl;
        break;
      case 'satisfaction_up':
        effects.satisfactionBonus += def.uniqueEffect.value;
        break;
      case 'emergency_patience':
        effects.emergencyPatienceRecovery += def.uniqueEffect.value * lvl;
        break;
      case 'revisit_rate_up':
        effects.revisitRateBonus += def.uniqueEffect.value;
        break;
      case 'reputation_per_day':
        effects.reputationPerDay += def.uniqueEffect.value * lvl;
        break;
      case 'anticipation_up':
        effects.anticipationBonus += def.uniqueEffect.value;
        break;
      case 'fun_bonus':
        effects.funBonus += def.uniqueEffect.value * lvl;
        break;
      case 'random_sp_bonus':
        effects.randomSpBonusChance += def.uniqueEffect.value;
        break;
    }
  }

  return effects;
}

/**
 * Calculate total patience modifier from all lobby building floors with facilities.
 * Returns a value 0-1 representing how much patience decay is reduced.
 */
function calcFacilityPatienceBonus(state: GameStateData): number {
  let total = 0;
  for (const floor of (state.lobbyBuilding || [])) {
    if (!floor.facilityType) continue;
    const def = LOBBY_FACILITY_DEFS[floor.facilityType];
    if (!def) continue;
    const levelBonus = def.patienceModifier + def.patiencePerLevel * (Math.max(1, floor.facilityLevel) - 1);
    // NPC staff bonus: +15% extra reduction
    const staffBonus = floor.assignedGhostId ? 0.15 : 0;
    total += levelBonus + staffBonus;
  }
  return Math.min(0.9, total); // Cap at 90% reduction
}

/**
 * Calculate total revenue per tick from lobby building floors based on queue size.
 */
function calcFacilityRevenue(state: GameStateData): number {
  const queueSize = (state.lobbyWaitQueue || []).length;
  if (queueSize === 0) return 0;

  let totalRev = 0;
  for (const floor of (state.lobbyBuilding || [])) {
    if (!floor.facilityType) continue;
    const def = LOBBY_FACILITY_DEFS[floor.facilityType];
    if (!def || def.revPerVisitor === 0) continue;
    const rev = (def.revPerVisitor + def.revenuePerLevel * (Math.max(1, floor.facilityLevel) - 1)) * queueSize;
    // NPC staff bonus: +20% revenue
    const staffMult = floor.assignedGhostId ? 1.2 : 1.0;
    totalRev += rev * staffMult;
  }
  return totalRev;
}

/**
 * Create a new QueueVisitor for the waiting queue.
 * Uses facility unique effects for VIP rate.
 */
function createQueueVisitor(state: GameStateData): QueueVisitor {
  const effects = calcLobbyFacilityEffects(state);
  const baseVipRate = 0.05;
  const vipRate = Math.min(0.5, baseVipRate * effects.vipRateMultiplier);
  const isVip = Math.random() < vipRate;
  const type = pickVisitorType(state, isVip);
  const visitorDef = VISITOR_TYPES[type];
  const basePatience = visitorDef.basePatience ?? 60;
  const variance = 0.8 + Math.random() * 0.4;
  const maxPat = Math.round(basePatience * variance);

  return {
    id: `qv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type,
    patience: maxPat,
    maxPatience: maxPat,
    arrivalTime: Date.now(),
    isVip,
  };
}

/**
 * Convert a QueueVisitor into a VisitorGroup for building entry.
 * Applies lobby facility unique effects (satisfaction, excitement, fun bonuses).
 */
function createGroupFromQueue(qv: QueueVisitor, state: GameStateData): VisitorGroup {
  _groupCounter++;
  const visitorDef = VISITOR_TYPES[qv.type];
  const variance = () => 0.8 + Math.random() * 0.4;
  const effects = calcLobbyFacilityEffects(state);

  // Satisfaction bonus from poster_wall
  const baseSatisfaction = Math.min(1, 0.5 + effects.satisfactionBonus);
  // Excitement bonus from tv_monitor (anticipation)
  const baseExcitement = visitorDef.baseExcitement + Math.round(effects.anticipationBonus * 100);

  const visitor: Visitor = {
    id: `v-${_groupCounter}-0`,
    type: qv.type,
    fearLevel: visitorDef.initialFear,
    fearTolerance: Math.round(visitorDef.fearTolerance * variance()),
    excitement: baseExcitement,
    nausea: 0,
    satisfaction: baseSatisfaction,
    budget: Math.round(visitorDef.baseBudget * variance()),
    patience: Math.round(visitorDef.basePatience * variance()),
    fled: false,
    currentReaction: null,
    reactionSetAt: 0,
  };

  return {
    id: `g-${_groupCounter}`,
    visitors: [visitor],
    currentFloorIndex: 0,
    currentRoomIndex: 0,
    timeAtRoomMs: 0,
    scareChainCount: 0,
    done: false,
    totalSpEarned: 0,
    roomsVisitedWithGhost: 0,
    isVip: qv.isVip,
    phase: 'entering' as const,
    phaseStartedAt: Date.now(),
  };
}

function lobbyQueueSystem(state: GameStateData, deltaMs: number): GameStateData {
  if (state.dayPhase !== 'evening') return state;

  let newState = { ...state };
  const queue = [...(newState.lobbyWaitQueue || [])];
  const effects = calcLobbyFacilityEffects(newState);

  // Apply waiting_lounge unique effect: max queue size bonus
  const maxQueue = (newState.maxQueueSize || 10) + effects.maxQueueBonus;

  // Apply ticket_booth unique effect: reduced spawn interval + time-based multiplier
  const timeMultiplier = getTimeBasedSpawnMultiplier(getInGameTime(newState));
  const spawnInterval = Math.max(2000, Math.round(QUEUE_SPAWN_INTERVAL_MS * (1 - effects.ticketSpeedBonus) / timeMultiplier));

  // Accumulate time for spawn
  newState.queueSpawnAccumMs = (newState.queueSpawnAccumMs || 0) + deltaMs;

  // 1. Spawn new visitors into queue at intervals
  if (newState.queueSpawnAccumMs >= spawnInterval) {
    newState.queueSpawnAccumMs -= spawnInterval;

    if (queue.length < maxQueue) {
      const newVisitor = createQueueVisitor(newState);

      // VIP visitors jump to front
      if (newVisitor.isVip && queue.length > 0) {
        queue.unshift(newVisitor);
      } else {
        queue.push(newVisitor);
      }
    }
  }

  // 2. Patience decay (every QUEUE_TICK_INTERVAL_MS)
  newState.queueTickAccumMs = (newState.queueTickAccumMs || 0) + deltaMs;

  if (newState.queueTickAccumMs >= QUEUE_TICK_INTERVAL_MS) {
    const ticks = Math.floor(newState.queueTickAccumMs / QUEUE_TICK_INTERVAL_MS);
    newState.queueTickAccumMs -= ticks * QUEUE_TICK_INTERVAL_MS;

    const patienceBonus = calcFacilityPatienceBonus(newState);
    const decayPerTick = 1 * (1 - patienceBonus);

    // Apply patience decay
    for (let i = queue.length - 1; i >= 0; i--) {
      queue[i] = {
        ...queue[i],
        patience: Math.max(0, queue[i].patience - decayPerTick * ticks),
      };
    }

    // Snack cart unique effect: emergency patience recovery for low-patience visitors
    if (effects.emergencyPatienceRecovery > 0) {
      const emergencyThreshold = 30;
      for (let i = 0; i < queue.length; i++) {
        if (queue[i].patience > 0 && queue[i].patience <= emergencyThreshold) {
          queue[i] = {
            ...queue[i],
            patience: Math.min(queue[i].maxPatience, queue[i].patience + effects.emergencyPatienceRecovery * ticks * 0.1),
          };
        }
      }
    }

    // 3. Remove visitors with 0 patience (they leave + bad reputation)
    const leavingCount = queue.filter((v) => v.patience <= 0).length;
    const surviving = queue.filter((v) => v.patience > 0);
    if (leavingCount > 0) {
      newState.reputation = Math.max(0, (newState.reputation || 50) - leavingCount * 1);
      // Splice out impatient visitors
      queue.length = 0;
      queue.push(...surviving);
    }

    // Facility revenue
    const revenue = calcFacilityRevenue(newState);
    if (revenue > 0) {
      const scaledRevenue = Math.round(revenue * ticks * 0.01); // Scale down per-tick
      if (scaledRevenue > 0) {
        newState.sp = (newState.sp || 0) + scaledRevenue;
        newState.todayEarnings = (newState.todayEarnings || 0) + scaledRevenue;
      }
    }

    // Fortune booth unique effect: random bonus SP
    if (effects.randomSpBonusChance > 0 && queue.length > 0) {
      if (Math.random() < effects.randomSpBonusChance * ticks * 0.01) {
        const bonusSp = Math.round(10 + Math.random() * 40);
        newState.sp = (newState.sp || 0) + bonusSp;
        newState.todayEarnings = (newState.todayEarnings || 0) + bonusSp;
      }
    }
  }

  // 4. If no active visitor group, promote front of queue
  if ((!newState.visitorGroup || newState.visitorGroup.done) && queue.length > 0) {
    const nextVisitor = queue.shift()!;
    const hasRoom = newState.building.some((f) => f.room !== null);
    if (hasRoom) {
      // Floor 1 blocked check
      const floor1 = newState.building[0];
      const floor1Blocked = floor1 && floor1.room && (floor1.room.collapsed || floor1.room.underRepair || floor1.room.underCleaning);

      if (!floor1Blocked) {
        newState.visitorGroup = createGroupFromQueue(nextVisitor, newState);
        newState.todayVisitors = (newState.todayVisitors || 0) + 1;
      } else {
        // Put visitor back at front
        queue.unshift(nextVisitor);
      }
    } else {
      // No rooms, put visitor back
      queue.unshift(nextVisitor);
    }
  }

  newState.lobbyWaitQueue = queue;
  return newState;
}

// ── Lobby NPC Ghost Scare System ───────────────────────────

const NPC_GHOST_SCARE_INTERVAL_MS = 30000; // 30 seconds between scares
const NPC_GHOST_PATIENCE_BONUS = 0.30;     // +30% patience recovery

function lobbyNpcGhostSystem(
  state: GameStateData,
  _deltaMs: number,
): GameStateData {
  if (state.dayPhase !== 'evening') return state;
  const npcGhosts = state.lobbyNpcGhosts || [];
  if (npcGhosts.length === 0) return state;
  const queue = state.lobbyWaitQueue || [];
  if (queue.length === 0) return state;

  const now = Date.now();
  let updatedGhosts = [...npcGhosts];
  let updatedQueue = [...queue];
  let changed = false;

  for (let gi = 0; gi < updatedGhosts.length; gi++) {
    const ng = updatedGhosts[gi];
    if (now - ng.lastScareAt < NPC_GHOST_SCARE_INTERVAL_MS) continue;

    // Pick a random visitor to scare
    if (updatedQueue.length === 0) break;
    const vi = Math.floor(Math.random() * updatedQueue.length);
    const visitor = updatedQueue[vi];
    const boost = visitor.maxPatience * NPC_GHOST_PATIENCE_BONUS;
    updatedQueue[vi] = {
      ...visitor,
      patience: Math.min(
        visitor.maxPatience,
        visitor.patience + boost,
      ),
    };
    updatedGhosts[gi] = { ...ng, lastScareAt: now };
    changed = true;
  }

  if (!changed) return state;
  return {
    ...state,
    lobbyNpcGhosts: updatedGhosts,
    lobbyWaitQueue: updatedQueue,
  };
}

// ── Random Event System ────────────────────────────────────

const EVENT_CHECK_INTERVAL_MS = 10000; // check every 10s
const EVENT_TRIGGER_CHANCE = 0.20;     // 20% per check

/** Pending event to show in UI */
let _pendingEvent: ActiveEvent | null = null;

export function consumePendingEvent(): ActiveEvent | null {
  const ev = _pendingEvent;
  _pendingEvent = null;
  return ev;
}

function randomEventSystem(
  state: GameStateData,
  deltaMs: number,
): GameStateData {
  // Only during evening phase
  if (state.dayPhase !== 'evening') return state;
  // Skip if event already active
  if (state.activeEvent) return state;

  let newState = { ...state };
  newState.eventTickAccumMs = (newState.eventTickAccumMs || 0) + deltaMs;

  if (newState.eventTickAccumMs < EVENT_CHECK_INTERVAL_MS) return newState;
  newState.eventTickAccumMs -= EVENT_CHECK_INTERVAL_MS;

  // Cooldown check
  const now = Date.now();
  if (now < (newState.eventCooldownUntil || 0)) return newState;

  // Roll dice
  if (Math.random() > EVENT_TRIGGER_CHANCE) return newState;

  // Find eligible events
  const eligible = getEligibleEvents(
    newState,
    newState.eventHistory || [],
  );
  if (eligible.length === 0) return newState;

  // Pick random
  const evt = eligible[Math.floor(Math.random() * eligible.length)];

  // Check defense condition
  const defended =
    evt.defense !== undefined && evt.defense.condition(newState);

  // If defended, apply reduced effect immediately and skip player choice
  if (defended && evt.defense) {
    const defPatch = evt.defense.reducedEffect
      ? evt.defense.reducedEffect(newState)
      : {};
    const bonus = evt.defense.bonusReward || 0;
    const history = [
      ...(newState.eventHistory || []),
      evt.id,
    ].slice(-10);

    const activeEvent: ActiveEvent = {
      eventId: evt.id,
      startedAt: now,
      expiresAt: 0,
      defended: true,
    };

    // Apply patch then add bonus SP on top
    const merged = { ...newState, ...defPatch };
    merged.sp = (merged.sp ?? 0) + bonus;
    merged.activeEvent = activeEvent;
    merged.eventHistory = history;
    merged.eventCooldownUntil = now + 30000;

    _pendingEvent = activeEvent;

    return merged;
  }

  const expiresAt = evt.duration > 0 ? now + evt.duration : 0;

  const activeEvent: ActiveEvent = {
    eventId: evt.id,
    startedAt: now,
    expiresAt,
    defended: false,
  };

  newState.activeEvent = activeEvent;
  _pendingEvent = activeEvent;

  return newState;
}

// ── Street Visitor System (RCT-style autonomous visitors) ──

const STREET_SPAWN_INTERVAL_MS = 2000;  // spawn new visitor every 2s
const STREET_AI_INTERVAL_MS = 1000;     // AI decision tick every 1s
const STREET_MOVE_SPEED = 0.08;         // position units per second
const STREET_USE_TIME_MS = 4000;        // time spent using a facility
const STREET_PATIENCE_BASE_MS = 60000;  // base patience: 60s
const NEED_GROWTH_PER_SEC = 1.5;        // needs grow per second
const NEED_THRESHOLD = 50;              // seek facility when need > this

/** Map facility types to which need they satisfy */
const FACILITY_NEED_MAP: Record<string, keyof StreetVisitorNeeds> = {
  haunted_house: 'fear_thrill',
  snack_bar: 'hunger',
  restroom: 'bathroom',
  gift_shop: 'souvenir',
  parking_lot: 'rest',
  first_aid: 'rest',
  photo_booth: 'souvenir',
  arcade: 'fear_thrill',
  fortune_teller: 'souvenir',
  escape_room: 'fear_thrill',
  stage: 'rest',
};

let _streetVisitorCounter = 0;

/** Visitor type profiles: initial needs + need growth rates + price sensitivity */
const VISITOR_TYPE_PROFILES: Record<StreetVisitorType, {
  needs: StreetVisitorNeeds;
  growthRates: StreetVisitorNeeds;
  priceSensitivity: number;   // 0-1: higher = more upset by expensive facilities
  spendingMultiplier: number; // multiplier on SP spent
  baseSatisfaction: number;
}> = {
  thrill_seeker: {
    needs: { fear_thrill: 80, hunger: 10, bathroom: 10, souvenir: 15, rest: 0 },
    growthRates: { fear_thrill: 0.6, hunger: 0.3, bathroom: 0.3, souvenir: 0.15, rest: 0.1 },
    priceSensitivity: 0.2,  // doesn't mind paying more
    spendingMultiplier: 1.3, // spends 30% more
    baseSatisfaction: 60,
  },
  family: {
    needs: { fear_thrill: 40, hunger: 50, bathroom: 30, souvenir: 40, rest: 20 },
    growthRates: { fear_thrill: 0.25, hunger: 0.6, bathroom: 0.5, souvenir: 0.35, rest: 0.3 },
    priceSensitivity: 0.7,  // very price sensitive
    spendingMultiplier: 0.8, // spends less per visit
    baseSatisfaction: 75,
  },
  couple: {
    needs: { fear_thrill: 55, hunger: 25, bathroom: 15, souvenir: 50, rest: 10 },
    growthRates: { fear_thrill: 0.4, hunger: 0.4, bathroom: 0.25, souvenir: 0.5, rest: 0.1 },
    priceSensitivity: 0.4,
    spendingMultiplier: 1.1,
    baseSatisfaction: 70,
  },
  vip: {
    needs: { fear_thrill: 90, hunger: 30, bathroom: 20, souvenir: 60, rest: 10 },
    growthRates: { fear_thrill: 0.5, hunger: 0.5, bathroom: 0.3, souvenir: 0.4, rest: 0.15 },
    priceSensitivity: 0.05,  // barely cares about price
    spendingMultiplier: 5.0, // spends 5x more
    baseSatisfaction: 50,    // starts lower — hard to please
  },
};

// ── Season Events ──────────────────────────────────────────

const SEASON_EVENT_DEFS: Record<SeasonEventId, SeasonEvent> = {
  halloween: {
    id: 'halloween',
    spawnMultiplier: 2.0,
    needBoosts: { fear_thrill: 30 },
    revenueMultiplier: 1.5,
    durationMs: 90_000,  // 90s game time
  },
  summer_fest: {
    id: 'summer_fest',
    spawnMultiplier: 1.5,
    needBoosts: { hunger: 25, rest: 15 },
    revenueMultiplier: 1.2,
    durationMs: 90_000,
  },
  winter_chill: {
    id: 'winter_chill',
    spawnMultiplier: 0.8,
    needBoosts: { souvenir: 20 },
    revenueMultiplier: 1.3,
    durationMs: 90_000,
  },
  spring_bloom: {
    id: 'spring_bloom',
    spawnMultiplier: 1.3,
    needBoosts: { souvenir: 15, rest: 10 },
    revenueMultiplier: 1.1,
    durationMs: 90_000,
  },
};

const SEASON_EVENT_IDS: SeasonEventId[] = ['halloween', 'summer_fest', 'winter_chill', 'spring_bloom'];

/** Word-of-mouth: adjust spawn rate based on average visitor satisfaction */
function updateWordOfMouth(visitors: StreetVisitor[], currentWom: number): number {
  if (visitors.length === 0) return currentWom;

  // Calculate average satisfaction of all visitors
  let totalSat = 0;
  for (const v of visitors) totalSat += v.satisfaction;
  const avgSat = totalSat / visitors.length;

  // Slowly drift toward target: high satisfaction → wom grows, low → shrinks
  const target = avgSat >= 70 ? 1.0 + (avgSat - 70) / 100  // max ~1.3 at sat=100
    : avgSat >= 40 ? 1.0
    : 0.7 + avgSat / 133;  // min ~0.7 at sat=0
  // Smooth lerp: 5% per tick toward target
  return currentWom + (target - currentWom) * 0.05;
}

/** Season event system: start/expire seasonal events */
function seasonEventSystem(state: GameStateData, _deltaMs: number): GameStateData {
  const now = Date.now();
  const currentEvent = state.seasonEvent;

  // Check if current event expired
  if (currentEvent && state.seasonEventStartedAt > 0) {
    const elapsed = now - state.seasonEventStartedAt;
    if (elapsed >= currentEvent.durationMs) {
      // Set cooldown: use seasonEventStartedAt as "last event ended at" marker (negative = cooldown)
      return { ...state, seasonEvent: null, seasonEventStartedAt: -now };
    }
    return state;
  }

  // Don't start a new event during cooldown (at least 60s between events)
  if (state.seasonEventStartedAt < 0) {
    const cooldownElapsed = now - (-state.seasonEventStartedAt);
    if (cooldownElapsed < 60_000) return state;
  }

  // Start new event: every 3rd day during evening phase
  if (!currentEvent && state.dayPhase === 'evening') {
    const dayNum = state.dayNumber || 1;
    if (dayNum % 3 === 0) {
      const eventIdx = Math.floor(dayNum / 3) % SEASON_EVENT_IDS.length;
      const eventId = SEASON_EVENT_IDS[eventIdx];
      const event = SEASON_EVENT_DEFS[eventId];
      return {
        ...state,
        seasonEvent: event,
        seasonEventStartedAt: now,
      };
    }
  }

  return state;
}

/** Pick 1-2 random VIP requirements */
const ALL_VIP_REQUIREMENTS: VipRequirementType[] = ['theme_match', 'staffed_only', 'no_wait', 'premium_tour', 'full_experience'];

function generateVipRequirements(): VipRequirement[] {
  const count = Math.random() < 0.4 ? 2 : 1; // 40% chance of 2 requirements
  const shuffled = [...ALL_VIP_REQUIREMENTS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map(type => ({ type, met: false }));
}

/** Check if VIP requirement is met for a specific facility visit */
function checkVipRequirement(
  req: VipRequirement,
  visitor: StreetVisitor,
  plot: { facility: GameStateData['facilityMap'][number]['facility'] } | null,
  state: GameStateData,
): boolean {
  if (req.met) return true;
  switch (req.type) {
    case 'theme_match':
      // Met when visitor uses a haunted house matching their preferred theme
      if (plot?.facility?.type === 'haunted_house' && plot.facility.buildingId && visitor.preferredTheme) {
        const meta = state.hauntedBuildingMetas?.find(m => m.id === plot.facility!.buildingId);
        return meta?.themeId === visitor.preferredTheme;
      }
      return false;
    case 'staffed_only':
      // Always met as long as every facility visited has staff (checked on each use)
      return !!plot?.facility?.assignedGhostId;
    case 'no_wait':
      // Met by default — fails only if visitor had to wait (checked externally)
      return true;
    case 'premium_tour': {
      // Met when visiting a haunted house with 3+ active rooms
      if (plot?.facility?.type !== 'haunted_house') return false;
      const buildingId = plot.facility.buildingId;
      const bld = buildingId ? state.hauntedBuildings.find(b => b.id === buildingId) : null;
      const building = bld ? bld.building : state.building;
      const activeRooms = building.filter(f => f.room?.assignedGhostId).length;
      return activeRooms >= 3;
    }
    case 'full_experience':
      // Met when visitor has used 3+ different facility types
      return (visitor.usedFacilityTypes?.length ?? 0) >= 3;
    default:
      return false;
  }
}

export function createStreetVisitor(availableThemes: string[]): StreetVisitor {
  _streetVisitorCounter++;
  // 70% chance to have a theme preference (from available building themes)
  const preferredTheme = availableThemes.length > 0 && Math.random() < 0.7
    ? availableThemes[Math.floor(Math.random() * availableThemes.length)]
    : undefined;

  // Pick visitor type: 40% thrill_seeker, 35% family, 25% couple
  // VIP spawns handled separately via reputation gate
  const roll = Math.random();
  const visitorType: StreetVisitorType = roll < 0.4 ? 'thrill_seeker' : roll < 0.75 ? 'family' : 'couple';
  const profile = VISITOR_TYPE_PROFILES[visitorType];

  return {
    id: `sv_${Date.now()}_${_streetVisitorCounter}`,
    visitorType,
    avatarIdx: Math.floor(Math.random() * 3),
    posY: 0,
    targetPlotId: -1,
    state: 'walking',
    needs: {
      fear_thrill: profile.needs.fear_thrill + Math.random() * 20 - 10,
      hunger: profile.needs.hunger + Math.random() * 20 - 10,
      bathroom: profile.needs.bathroom + Math.random() * 15,
      souvenir: profile.needs.souvenir + Math.random() * 20 - 10,
      rest: profile.needs.rest + Math.random() * 10,
    },
    totalSpent: 0,
    timeOnStreetMs: 0,
    patienceMs: STREET_PATIENCE_BASE_MS + Math.random() * 30000,
    useTimeRemainingMs: 0,
    satisfaction: profile.baseSatisfaction,
    preferredTheme,
  };
}

/** Create a VIP visitor — spawned separately, with special requirements */
export function createVipVisitor(availableThemes: string[]): StreetVisitor {
  _streetVisitorCounter++;
  const preferredTheme = availableThemes.length > 0
    ? availableThemes[Math.floor(Math.random() * availableThemes.length)]
    : undefined;
  const profile = VISITOR_TYPE_PROFILES.vip;

  return {
    id: `vip_${Date.now()}_${_streetVisitorCounter}`,
    visitorType: 'vip',
    avatarIdx: Math.floor(Math.random() * 3),
    posY: 0,
    targetPlotId: -1,
    state: 'walking',
    needs: {
      fear_thrill: profile.needs.fear_thrill + Math.random() * 10,
      hunger: profile.needs.hunger + Math.random() * 15,
      bathroom: profile.needs.bathroom + Math.random() * 10,
      souvenir: profile.needs.souvenir + Math.random() * 15,
      rest: profile.needs.rest + Math.random() * 10,
    },
    totalSpent: 0,
    timeOnStreetMs: 0,
    patienceMs: 45000 + Math.random() * 15000, // VIPs are less patient (45-60s)
    useTimeRemainingMs: 0,
    satisfaction: profile.baseSatisfaction,
    preferredTheme,
    vipRequirements: generateVipRequirements(),
    usedFacilityTypes: [],
  };
}

/** Find the highest unmet need and a matching facility plot */
function pickFacilityForVisitor(
  visitor: StreetVisitor,
  facilityMap: GameStateData['facilityMap'],
  buildingMetas: GameStateData['hauntedBuildingMetas'],
  queueFull?: boolean,
  buildings?: GameStateData['hauntedBuildings'],
): number {
  // Collect needs above threshold, sorted by urgency
  const urgentNeeds: { need: keyof StreetVisitorNeeds; value: number }[] = [];
  for (const [key, val] of Object.entries(visitor.needs)) {
    if (val >= NEED_THRESHOLD) {
      urgentNeeds.push({ need: key as keyof StreetVisitorNeeds, value: val });
    }
  }
  urgentNeeds.sort((a, b) => b.value - a.value);

  // Helper: check if a specific building's queue has room
  const isBuildingQueueFull = (buildingId?: string): boolean => {
    if (!buildingId || !buildings) return false;
    const bld = buildings.find(b => b.id === buildingId);
    if (!bld) return false;
    return (bld.lobbyWaitQueue || []).length >= (bld.maxQueueSize || 10);
  };

  // Try to find an available facility for the most urgent need
  for (const { need } of urgentNeeds) {
    // Skip all haunted houses if explicitly told queue is full (fallback mode)
    if (need === 'fear_thrill' && queueFull) continue;

    if (need === 'fear_thrill') {
      // For haunted houses, prefer the one matching visitor's theme preference
      const hauntedPlots = facilityMap.filter(p =>
        p.unlocked && p.facility?.type === 'haunted_house' && p.facility.buildingId && p.facility.isOpen !== false
      );

      // Filter to buildings with available queue space
      const availablePlots = hauntedPlots.filter(p => !isBuildingQueueFull(p.facility!.buildingId));

      if (availablePlots.length > 0 && visitor.preferredTheme) {
        // Find building with matching theme
        const preferred = availablePlots.find(p => {
          const meta = buildingMetas?.find(m => m.id === p.facility!.buildingId);
          return meta?.themeId === visitor.preferredTheme;
        });
        if (preferred) return preferred.id;
      }
      // No theme match — pick random available haunted house
      if (availablePlots.length > 0) {
        return availablePlots[Math.floor(Math.random() * availablePlots.length)].id;
      }
      // All queues full — skip to auxiliary facilities
      if (hauntedPlots.length > 0 && availablePlots.length === 0) continue;
    }
    for (const plot of facilityMap) {
      if (!plot.unlocked || !plot.facility || plot.facility.isOpen === false) continue;
      if (plot.facility.type === 'ticket_booth') continue; // ticket booth is not visitable
      const facNeed = FACILITY_NEED_MAP[plot.facility.type];
      if (facNeed === need) return plot.id;
    }
  }

  // All haunted queues full — pick any non-haunted-house facility to wander
  const auxiliaryPlots = facilityMap.filter(p =>
    p.unlocked && p.facility && p.facility.type !== 'haunted_house' && p.facility.type !== 'ticket_booth' && p.facility.isOpen !== false
  );
  if (auxiliaryPlots.length > 0) {
    return auxiliaryPlots[Math.floor(Math.random() * auxiliaryPlots.length)].id;
  }

  // No matching facility — just wander (pick random unlocked plot or exit)
  return -1;
}

/** Convert plot id to a Y position (0-1 range, plot 0 = near entrance) */
function plotIdToY(plotId: number): number {
  const row = Math.floor(plotId / 2);
  return (row + 0.5) / 8;  // 8 rows, center of each row
}

/** Sync unlocked buildings to facility map plots (auto-register new haunted houses) */
function syncBuildingsToFacilityMap(state: GameStateData): GameStateData {
  const metas = state.hauntedBuildingMetas || [];
  const unlockedBuildings = metas.filter(m => m.isUnlocked && m.themeId);

  // Find which buildings already have a plot
  const placedBuildingIds = new Set<string>();
  for (const plot of state.facilityMap) {
    if (plot.facility?.type === 'haunted_house' && plot.facility.buildingId) {
      placedBuildingIds.add(plot.facility.buildingId);
    }
  }

  // Find buildings that need a plot
  const unplaced = unlockedBuildings.filter(m => !placedBuildingIds.has(m.id));
  if (unplaced.length === 0) return state;

  // Place them in empty unlocked plots (or auto-unlock next plot)
  let facilityMap = state.facilityMap.map(p => ({ ...p, facility: p.facility ? { ...p.facility } : null }));
  for (const meta of unplaced) {
    let emptyIdx = facilityMap.findIndex(p => p.unlocked && !p.facility);
    if (emptyIdx < 0) {
      // Auto-unlock next locked plot
      emptyIdx = facilityMap.findIndex(p => !p.unlocked);
      if (emptyIdx < 0) break; // all plots full
      facilityMap[emptyIdx] = { ...facilityMap[emptyIdx], unlocked: true };
    }
    facilityMap[emptyIdx] = {
      ...facilityMap[emptyIdx],
      facility: {
        type: 'haunted_house',
        upgradeLevel: 1,
        assignedGhostId: null,
        props: [],
        price: 10,
        totalRevenue: 0,
        isOpen: true,
        buildingId: meta.id,
        totalRatings: 0,
        ratingSum: 0,
        visitorCount: 0,
      },
    };
  }

  return { ...state, facilityMap };
}

/** Calculate SP earned from a simulated building tour (street visitor using haunted_house) */
function calcBuildingTourSp(state: GameStateData, buildingId?: string): { sp: number; satisfactionDelta: number } {
  // Find the building data
  let building = state.building;
  let ticketPrice = state.ticketPrice;
  let storyBonus = state.storyBonus ?? 0;
  let themeId = getSelectedBuildingThemeId(state);

  if (buildingId) {
    const bld = state.hauntedBuildings.find(b => b.id === buildingId);
    if (bld) {
      building = bld.building;
      ticketPrice = bld.ticketPrice;
      storyBonus = bld.storyBonus;
      themeId = bld.themeId || themeId;
    }
  }

  if (!building || building.length === 0) return { sp: 0, satisfactionDelta: -5 };

  // Count rooms with ghosts assigned (active scare rooms)
  let activeRooms = 0;
  let totalBaseSp = 0;

  for (const floor of building) {
    if (!floor.room || !floor.room.assignedGhostId) continue;
    activeRooms++;

    const ghost = state.ghosts.find(g => g.id === floor.room!.assignedGhostId);
    if (!ghost) continue;
    const ghostDef = GHOST_DEFS[ghost.characterId];
    if (!ghostDef) continue;

    const roomDef = ROOM_TYPES[floor.room.type];
    if (!roomDef) continue;
    const upgradeMultiplier = CONFIG.roomUpgradeMultipliers[(floor.room.upgradeLevel || 1) - 1] ?? 1.0;
    const levelMultiplier = 1 + (ghost.level - 1) * CONFIG.ghostLevelBonusPerLevel;
    const fatigueMultiplier = ghost.fatigue >= CONFIG.fatiguePenaltyThreshold ? CONFIG.fatiguePenaltyMultiplier : 1.0;
    const themeBonus = themeId && roomDef.themeTags?.includes(themeId) ? 1.15 : 1.0;

    totalBaseSp += ghostDef.baseFear * upgradeMultiplier * levelMultiplier * fatigueMultiplier * themeBonus;
  }

  if (activeRooms === 0) return { sp: 0, satisfactionDelta: -10 };

  // Apply ticket price and story bonus
  const ticketMultiplier = ticketPrice / 10;
  const storyMultiplier = 1 + storyBonus / 100;
  const sp = Math.max(1, Math.round(totalBaseSp * ticketMultiplier * storyMultiplier));

  // Satisfaction based on rooms and price
  const roomRatio = activeRooms / Math.max(1, building.length);
  const priceRatio = ticketPrice / Math.max(1, activeRooms * 5);
  let satisfactionDelta = Math.round(roomRatio * 15);
  if (priceRatio > 3) satisfactionDelta -= 5;
  if (priceRatio < 1) satisfactionDelta += 3;

  return { sp, satisfactionDelta: Math.max(-10, Math.min(20, satisfactionDelta)) };
}

function streetVisitorSystem(state: GameStateData, deltaMs: number): GameStateData {
  if (state.dayPhase !== 'evening') {
    // Only active during business hours — generate final reviews before clearing
    if (state.streetVisitors.length > 0) {
      const finalReviews: GameStateData['reviews'][number][] = [];
      let finalRepDelta = 0;
      let finalSp = state.sp;
      let finalEarnings = 0;
      for (const v of state.streetVisitors) {
        // VIP final departure
        if (v.visitorType === 'vip') {
          const allReqsMet = v.vipRequirements?.every(r => r.met) ?? false;
          if (allReqsMet && v.satisfaction >= 60) {
            const bonus = Math.round(v.totalSpent * 2);
            finalSp += bonus;
            finalEarnings += bonus;
            finalRepDelta += 3.0;
            finalReviews.push({ id: `vip_end_${v.id.slice(-4)}`, stars: 5 as 1|2|3|4|5, textKey: 'review_vip_satisfied', ghostNameKey: null, timestamp: Date.now(), satisfaction: v.satisfaction });
          } else {
            finalRepDelta -= 3.0;
            finalReviews.push({ id: `vip_end_${v.id.slice(-4)}`, stars: 1 as 1|2|3|4|5, textKey: 'review_vip_unsatisfied', ghostNameKey: null, timestamp: Date.now(), satisfaction: v.satisfaction });
          }
        } else if (v.totalSpent > 0 && Math.random() < 0.3) {
          const stars = v.satisfaction >= 80 ? 5 : v.satisfaction >= 60 ? 4 : v.satisfaction >= 40 ? 3 : v.satisfaction >= 20 ? 2 : 1;
          finalReviews.push({ id: `sr_end_${v.id.slice(-4)}`, stars: stars as 1|2|3|4|5, textKey: `review_street_${stars}star`, ghostNameKey: null, timestamp: Date.now(), satisfaction: v.satisfaction });
          if (stars >= 4) finalRepDelta += 0.3;
          else if (stars <= 2) finalRepDelta -= 0.3;
        }
      }
      const result: Partial<GameStateData> = {
        streetVisitors: [],
        streetSpawnAccumMs: 0,
        streetAiAccumMs: 0,
        sp: finalSp,
        todayEarnings: state.todayEarnings + finalEarnings,
        reputation: Math.max(0, Math.min(100, state.reputation + finalRepDelta)),
      };
      if (finalReviews.length > 0) result.reviews = [...state.reviews, ...finalReviews].slice(-50);
      return { ...state, ...result };
    }
    return state;
  }

  // Apply gameSpeed to deltaMs for street visitors
  const speed = state.gameSpeed || 1;
  const scaledDeltaMs = deltaMs * speed;

  let visitors = [...state.streetVisitors];
  let sp = state.sp;
  let todayEarnings = 0;      // additional earnings this tick
  let totalSpEarned = 0;      // additional total SP earned
  let todayVisitors = 0;      // additional visitors counted
  let reputationDelta = 0;    // reputation change this tick
  let spawnAccum = state.streetSpawnAccumMs + scaledDeltaMs;
  let aiAccum = state.streetAiAccumMs + scaledDeltaMs;
  let facilityMap = state.facilityMap;
  let facilityMapDirty = false;
  const newReviews: GameStateData['reviews'][number][] = [];

  // Dynamic max: base + facilities + reputation + ticket booth level + fame tier
  const builtFacilities = facilityMap.filter(p => p.facility).length;
  const lobbyBonus = Math.floor((state.maxQueueSize || 10) / 5);
  const lobbyFloorBonus = (state.lobbyBuilding || []).length;
  const tbPlot = facilityMap.find(p => p.facility?.type === 'ticket_booth');
  const tbLevel = tbPlot?.facility?.upgradeLevel ?? 1;
  const ticketBoothCapBonus = (tbLevel - 1) * 5; // +5 max per level
  const fameTier = (state.fame || 0) < 50 ? 0 : (state.fame || 0) < 200 ? 3 : (state.fame || 0) < 500 ? 8 : 15;
  const dynamicMax = 10 + builtFacilities * 2 + Math.floor(state.reputation / 10) + lobbyBonus + lobbyFloorBonus + ticketBoothCapBonus + fameTier;

  // Collect available themes from placed haunted buildings
  const availableThemes: string[] = [];
  for (const plot of facilityMap) {
    if (plot.facility?.type === 'haunted_house' && plot.facility.buildingId) {
      const meta = (state.hauntedBuildingMetas || []).find(m => m.id === plot.facility!.buildingId);
      if (meta?.themeId) availableThemes.push(meta.themeId);
    }
  }

  // Fame-based spawn multiplier: more famous = more visitors
  const fame = state.fame || 0;
  const fameMultiplier = fame < 50 ? 0.3 + (fame / 50) * 0.7
    : fame < 200 ? 1.0 + ((fame - 50) / 150) * 0.5
    : fame < 500 ? 1.5 + ((fame - 200) / 300) * 0.5
    : 2.0;

  // Ticket booth level bonus: higher level = faster spawn + more capacity
  const ticketBoothPlot = state.facilityMap?.find(p => p.facility?.type === 'ticket_booth');
  const ticketBoothLevel = ticketBoothPlot?.facility?.upgradeLevel ?? 1;
  const ticketBoothSpawnBonus = 1 + (ticketBoothLevel - 1) * 0.15; // +15% per level

  // Word-of-mouth, season event, and promoter staff affect spawn rate
  const womMultiplier = state.wordOfMouth ?? 1.0;
  const seasonSpawnMult = state.seasonEvent?.spawnMultiplier ?? 1.0;
  const seasonRevenueMult = state.seasonEvent?.revenueMultiplier ?? 1.0;
  const promoterBonus = calcPromoterBonus(state); // +20% spawn rate per promoter level
  const securityBonus = calcSecurityBonus(state); // +10% satisfaction per security level
  const streetTimeMultiplier = getTimeBasedSpawnMultiplier(getInGameTime(state));
  const effectiveSpawnInterval = Math.max(500, STREET_SPAWN_INTERVAL_MS / (fameMultiplier * ticketBoothSpawnBonus * womMultiplier * seasonSpawnMult * (1 + promoterBonus) * streetTimeMultiplier));

  // --- Spawn new visitors ---
  while (spawnAccum >= effectiveSpawnInterval && visitors.length < dynamicMax) {
    spawnAccum -= effectiveSpawnInterval;
    // VIP spawn: reputation >= 50, 10% chance per spawn, max 2 VIPs at a time
    const currentVipCount = visitors.filter(v => v.visitorType === 'vip').length;
    const isVipSpawn = state.reputation >= 50 && Math.random() < 0.1 && currentVipCount < 2;
    const newVisitor = isVipSpawn
      ? createVipVisitor(availableThemes)
      : createStreetVisitor(availableThemes);
    // Season event need boosts
    if (state.seasonEvent?.needBoosts) {
      for (const [k, v] of Object.entries(state.seasonEvent.needBoosts)) {
        const key = k as keyof StreetVisitorNeeds;
        if (key in newVisitor.needs) {
          newVisitor.needs[key] = Math.min(100, newVisitor.needs[key] + (v ?? 0));
        }
      }
    }
    visitors.push(newVisitor);
  }
  if (spawnAccum >= effectiveSpawnInterval) spawnAccum = 0; // cap if at max

  // --- Ad-triggered VIP spawn ---
  if (state.pendingVipSpawn) {
    visitors.push(createVipVisitor(availableThemes));
  }

  // --- AI tick (decision making) ---
  const doAi = aiAccum >= STREET_AI_INTERVAL_MS;
  if (doAi) aiAccum -= STREET_AI_INTERVAL_MS;

  const deltaSec = scaledDeltaMs / 1000;
  const updatedVisitors: StreetVisitor[] = [];

  for (const v of visitors) {
    let visitor = { ...v, needs: { ...v.needs } };
    visitor.timeOnStreetMs += scaledDeltaMs;

    // Grow needs over time (type-specific growth rates)
    const rates = VISITOR_TYPE_PROFILES[visitor.visitorType]?.growthRates;
    const gr = rates || VISITOR_TYPE_PROFILES.thrill_seeker.growthRates;
    visitor.needs.hunger = Math.min(100, visitor.needs.hunger + NEED_GROWTH_PER_SEC * deltaSec * gr.hunger);
    visitor.needs.bathroom = Math.min(100, visitor.needs.bathroom + NEED_GROWTH_PER_SEC * deltaSec * gr.bathroom);
    visitor.needs.souvenir = Math.min(100, visitor.needs.souvenir + NEED_GROWTH_PER_SEC * deltaSec * gr.souvenir);
    visitor.needs.rest = Math.min(100, visitor.needs.rest + NEED_GROWTH_PER_SEC * deltaSec * gr.rest);
    visitor.needs.fear_thrill = Math.min(100, visitor.needs.fear_thrill + NEED_GROWTH_PER_SEC * deltaSec * gr.fear_thrill);

    // Check patience
    if (visitor.timeOnStreetMs > visitor.patienceMs) {
      visitor.state = 'leaving';
      visitor.targetPlotId = -2;
    }

    if (visitor.state === 'using') {
      visitor.useTimeRemainingMs -= scaledDeltaMs;
      if (visitor.useTimeRemainingMs <= 0) {
        // Done using facility — satisfy the need
        const plotIdx = facilityMap.findIndex(p => p.id === visitor.targetPlotId);
        const plot = plotIdx >= 0 ? facilityMap[plotIdx] : null;
        if (plot?.facility) {
          const needKey = FACILITY_NEED_MAP[plot.facility.type];
          if (needKey) {
            visitor.needs[needKey] = Math.max(0, visitor.needs[needKey] - 70);
          }

          // Track used facility types (for VIP full_experience requirement)
          if (visitor.usedFacilityTypes !== undefined) {
            if (!visitor.usedFacilityTypes.includes(plot.facility.type)) {
              visitor.usedFacilityTypes = [...visitor.usedFacilityTypes, plot.facility.type];
            }
          }

          // VIP requirement checks
          if (visitor.visitorType === 'vip' && visitor.vipRequirements) {
            visitor.vipRequirements = visitor.vipRequirements.map(req => {
              if (req.met) return req;
              const met = checkVipRequirement(req, visitor, { facility: plot.facility }, state);
              return met ? { ...req, met: true } : req;
            });
            // staffed_only: fail if this facility has no staff
            const staffedReq = visitor.vipRequirements.find(r => r.type === 'staffed_only');
            if (staffedReq && !staffedReq.met && !plot.facility.assignedGhostId) {
              visitor.satisfaction = Math.max(0, visitor.satisfaction - 15);
            }
          }

          // Calculate facility bonuses: staff, upgrade level, props, visitor type
          const vProfile = VISITOR_TYPE_PROFILES[visitor.visitorType] || VISITOR_TYPE_PROFILES.thrill_seeker;
          const hasStaff = !!plot.facility.assignedGhostId;
          const staffRevenueMultiplier = hasStaff ? 1.2 : 1.0;  // +20% revenue with staff
          const staffSatisfactionBonus = hasStaff ? 5 : 0;       // +5 satisfaction with staff
          const upgradeLevelMultiplier = 1 + (plot.facility.upgradeLevel - 1) * 0.15; // +15% per upgrade
          let propsBonusPct = 0;
          for (const prop of plot.facility.props) {
            const propDef = FACILITY_PROP_DEFS[prop.propId];
            if (propDef) propsBonusPct += propDef.bonusPct * prop.level;
          }
          const propsMultiplier = 1 + propsBonusPct;
          const totalBonusMultiplier = staffRevenueMultiplier * upgradeLevelMultiplier * propsMultiplier * vProfile.spendingMultiplier * seasonRevenueMult;

          // Price sensitivity: high prices reduce satisfaction for sensitive visitors
          const basePrice = plot.facility.price ?? 0;
          if (basePrice > 15) {
            const pricePenalty = Math.floor((basePrice - 15) * vProfile.priceSensitivity * 0.5);
            visitor.satisfaction = Math.max(10, visitor.satisfaction - pricePenalty);
          } else if (basePrice <= 5 && basePrice > 0) {
            // Cheap prices make visitors happier
            visitor.satisfaction = Math.min(100, visitor.satisfaction + 2);
          }

          if (plot.facility.type === 'haunted_house') {
            // === Haunted House Tour: SP based on building quality ===
            const tourResult = calcBuildingTourSp(state, plot.facility.buildingId);
            const tourSp = Math.round(tourResult.sp * totalBonusMultiplier);
            if (tourSp > 0) {
              sp += tourSp;
              visitor.totalSpent += tourSp;
              todayEarnings += tourSp;
              totalSpEarned += tourSp;
              if (!facilityMapDirty) {
                facilityMap = facilityMap.map(p => ({ ...p, facility: p.facility ? { ...p.facility } : null }));
                facilityMapDirty = true;
              }
              facilityMap[plotIdx].facility!.totalRevenue += tourSp;
            }
            // Satisfaction based on tour quality + staff bonus + security bonus
            visitor.satisfaction = Math.min(100, visitor.satisfaction + tourResult.satisfactionDelta + staffSatisfactionBonus + Math.round(securityBonus * 10));
            // Reputation change
            reputationDelta += tourResult.satisfactionDelta > 5 ? 0.3 : tourResult.satisfactionDelta > 0 ? 0.1 : -0.2;
            todayVisitors += 1;
          } else {
            // Regular facility: earn SP from price * bonuses
            const facPrice = plot.facility.price ?? 0;
            const price = Math.round(facPrice * totalBonusMultiplier);
            if (price > 0) {
              sp += price;
              visitor.totalSpent += price;
              todayEarnings += price;
              if (!facilityMapDirty) {
                facilityMap = facilityMap.map(p => ({ ...p, facility: p.facility ? { ...p.facility } : null }));
                facilityMapDirty = true;
              }
              facilityMap[plotIdx].facility!.totalRevenue += price;
            }
            visitor.satisfaction = Math.min(100, visitor.satisfaction + 5 + staffSatisfactionBonus + Math.round(securityBonus * 10));
          }

          // Update facility rating from visitor satisfaction
          if (!facilityMapDirty) {
            facilityMap = facilityMap.map(p => ({ ...p, facility: p.facility ? { ...p.facility } : null }));
            facilityMapDirty = true;
          }
          const fPlot = facilityMap[plotIdx].facility!;
          const rating = visitor.satisfaction >= 80 ? 5 : visitor.satisfaction >= 60 ? 4 : visitor.satisfaction >= 40 ? 3 : visitor.satisfaction >= 20 ? 2 : 1;
          fPlot.totalRatings = (fPlot.totalRatings || 0) + 1;
          fPlot.ratingSum = (fPlot.ratingSum || 0) + rating;
          fPlot.visitorCount = (fPlot.visitorCount || 0) + 1;
        }
        visitor.state = 'walking';
        visitor.targetPlotId = -1;
      }
      updatedVisitors.push(visitor);
      continue;
    }

    if (visitor.state === 'leaving') {
      // Move toward exit (posY → 0)
      visitor.posY = Math.max(0, visitor.posY - STREET_MOVE_SPEED * deltaSec);
      if (visitor.posY <= 0.01) {
        // Visitor has left — generate review + reputation impact
        const didSpend = visitor.totalSpent > 0;
        const wasImpatient = visitor.timeOnStreetMs >= visitor.patienceMs;

        // VIP departure — big rewards or big penalties
        if (visitor.visitorType === 'vip') {
          const allReqsMet = visitor.vipRequirements?.every(r => r.met) ?? false;
          const vipSatisfied = allReqsMet && visitor.satisfaction >= 60;

          if (vipSatisfied) {
            // VIP happy: big SP bonus + reputation boost
            const vipBonus = Math.round(visitor.totalSpent * 2); // 2x bonus on top
            sp += vipBonus;
            todayEarnings += vipBonus;
            totalSpEarned += vipBonus;
            reputationDelta += 3.0;
            newReviews.push({
              id: `vip_${Date.now()}_${visitor.id.slice(-4)}`,
              stars: 5 as 1|2|3|4|5,
              textKey: 'review_vip_satisfied',
              ghostNameKey: null,
              timestamp: Date.now(),
              satisfaction: visitor.satisfaction,
            });
          } else {
            // VIP unhappy: reputation tanked
            reputationDelta -= 3.0;
            newReviews.push({
              id: `vip_${Date.now()}_${visitor.id.slice(-4)}`,
              stars: 1 as 1|2|3|4|5,
              textKey: 'review_vip_unsatisfied',
              ghostNameKey: null,
              timestamp: Date.now(),
              satisfaction: visitor.satisfaction,
            });
          }
          continue;
        }

        // Regular visitor departure
        // Impatient departure = bad word-of-mouth (reputation penalty)
        if (wasImpatient && !didSpend) {
          reputationDelta -= 0.5;
        }

        if (didSpend && Math.random() < 0.3) {
          const stars = visitor.satisfaction >= 80 ? 5
            : visitor.satisfaction >= 60 ? 4
            : visitor.satisfaction >= 40 ? 3
            : visitor.satisfaction >= 20 ? 2 : 1;
          newReviews.push({
            id: `sr_${Date.now()}_${visitor.id.slice(-4)}`,
            stars: stars as 1|2|3|4|5,
            textKey: `review_street_${stars}star`,
            ghostNameKey: null,
            timestamp: Date.now(),
            satisfaction: visitor.satisfaction,
          });
          // Good reviews boost reputation, bad ones hurt
          if (stars >= 4) reputationDelta += 0.3;
          else if (stars <= 2) reputationDelta -= 0.3;
        }
        continue;
      }
      updatedVisitors.push(visitor);
      continue;
    }

    // Walking state
    if (doAi && visitor.targetPlotId === -1) {
      // Check if ALL building queues are full
      let allQueuesFull = true;
      if (state.hauntedBuildings && state.hauntedBuildings.length > 0) {
        for (const bld of state.hauntedBuildings) {
          if ((bld.lobbyWaitQueue || []).length < (bld.maxQueueSize || 10)) {
            allQueuesFull = false;
            break;
          }
        }
      } else {
        const effects = calcLobbyFacilityEffects(state);
        const maxQueue = (state.maxQueueSize || 10) + effects.maxQueueBonus;
        allQueuesFull = (state.lobbyWaitQueue || []).length >= maxQueue;
      }

      // Pick a destination (skip haunted house if all queues are full)
      const plotId = pickFacilityForVisitor(visitor, facilityMap, state.hauntedBuildingMetas, allQueuesFull, state.hauntedBuildings);
      if (plotId >= 0) {
        visitor.targetPlotId = plotId;
      } else {
        // No facility matches — wander a bit, then consider leaving
        if (visitor.timeOnStreetMs > visitor.patienceMs * 0.7) {
          visitor.state = 'leaving';
          visitor.targetPlotId = -2;
        }
      }
    }

    // Move toward target
    if (visitor.targetPlotId >= 0) {
      const targetY = plotIdToY(visitor.targetPlotId);
      const diff = targetY - visitor.posY;
      if (Math.abs(diff) < 0.02) {
        // Arrived at facility
        visitor.posY = targetY;

        // Haunted house queue integration: check per-building queue capacity
        const targetPlot = facilityMap[visitor.targetPlotId];
        if (targetPlot?.facility?.type === 'haunted_house') {
          // Find the specific building this plot links to
          const bldId = targetPlot.facility.buildingId;
          const bld = bldId ? state.hauntedBuildings.find(b => b.id === bldId) : null;
          const bldQueue = bld ? (bld.lobbyWaitQueue || []) : (state.lobbyWaitQueue || []);
          const bldMaxQueue = bld ? (bld.maxQueueSize || 10) : (state.maxQueueSize || 10);
          const effects = calcLobbyFacilityEffects(state);
          const maxQueue = bldMaxQueue + effects.maxQueueBonus;
          const queueLen = bldQueue.length;

          if (queueLen >= maxQueue) {
            // Queue full — visitor can't enter, try to find an auxiliary facility
            const auxPlotId = pickFacilityForVisitor(visitor, facilityMap, state.hauntedBuildingMetas, true, state.hauntedBuildings);
            if (auxPlotId >= 0) {
              visitor.targetPlotId = auxPlotId;
              visitor.state = 'walking';
            } else {
              visitor.targetPlotId = -1;
              visitor.state = 'walking';
            }
            visitor.satisfaction = Math.max(0, visitor.satisfaction - 5);
            reputationDelta -= 0.2;
            continue;
          }
          // Queue has room — visitor enters building queue
        }

        visitor.state = 'using';
        visitor.useTimeRemainingMs = STREET_USE_TIME_MS + Math.random() * 2000;
      } else {
        visitor.posY += Math.sign(diff) * STREET_MOVE_SPEED * deltaSec;
      }
    } else {
      // Wander slowly
      visitor.posY += (Math.random() - 0.4) * STREET_MOVE_SPEED * deltaSec * 0.3;
      visitor.posY = Math.max(0.05, Math.min(0.95, visitor.posY));
    }

    updatedVisitors.push(visitor);
  }

  // Update word-of-mouth based on current visitors' satisfaction
  const newWordOfMouth = updateWordOfMouth(updatedVisitors, state.wordOfMouth ?? 1.0);

  const result: Partial<GameStateData> = {
    sp,
    streetVisitors: updatedVisitors,
    streetSpawnAccumMs: spawnAccum,
    streetAiAccumMs: aiAccum,
    todayEarnings: state.todayEarnings + todayEarnings,
    totalSpEarned: state.totalSpEarned + totalSpEarned,
    todayVisitors: state.todayVisitors + todayVisitors,
    reputation: Math.max(0, Math.min(100, state.reputation + reputationDelta)),
    wordOfMouth: Math.max(0.5, Math.min(2.0, newWordOfMouth)),
    pendingVipSpawn: false,
  };
  if (facilityMapDirty) result.facilityMap = facilityMap;
  if (newReviews.length > 0) result.reviews = [...state.reviews, ...newReviews].slice(-50);
  return { ...state, ...result };
}

export function tick(state: GameStateData, deltaMs: number): GameStateData {
  let s = state;
  s = dayPhaseSystem(s, deltaMs);
  s = timePhaseSystem(s, deltaMs);
  s = repairCleaningSystem(s, deltaMs);
  s = ghostStaffSystem(s, deltaMs);
  s = lobbyQueueSystem(s, deltaMs);
  s = lobbyNpcGhostSystem(s, deltaMs);
  s = randomEventSystem(s, deltaMs);
  s = processVisitorSystem(s, deltaMs);
  s = fatigueRecoverySystem(s, deltaMs);
  s = popupCleanupSystem(s, deltaMs);
  s = adBoostCleanupSystem(s, deltaMs);
  s = updateMilestonesSystem(s);
  s = syncBuildingsToFacilityMap(s);
  s = seasonEventSystem(s, deltaMs);
  s = streetVisitorSystem(s, deltaMs);
  return s;
}

// ── Offline Earnings Calculator (removed) ──────────────────

// ── Browser-only: rAF game loop ────────────────────────────

let lastTime = 0;
let animFrameId = 0;

export function startGameLoop(
  getState: () => GameStateData,
  setState: (s: GameStateData) => void,
): void {
  const loop = (now: number) => {
    const deltaMs = lastTime ? now - lastTime : 0;
    lastTime = now;
    setState(tick(getState(), deltaMs));
    animFrameId = requestAnimationFrame(loop);
  };
  animFrameId = requestAnimationFrame(loop);
}

export function stopGameLoop(): void {
  cancelAnimationFrame(animFrameId);
  lastTime = 0;
}
